<template>
  <div class="tournament-rank-view pa-7" :class="{ 'tournament-rank-embed': isEmbedView }" :style="parentBackgroundStyle">
    <v-overlay :dark="false" :absolute="true" z-index="10" :value="overlayProcessing"><v-progress-circular
        color="swisetGreen" :size="70" width="5" indeterminate /></v-overlay>

    <!-- Display embed view for embedded/public version -->
    <TournamentEmbedView v-if="isEmbedView && $store.state.tournament"
      :tournament-data="$store.state.tournament"
      :tournament-closed="tournamentStatus === 'CLOSED'"
      :tournament-rules="tournamentRules"
      :serverTotalPages="participantsTotalPages"
      :serverCurrentPage="participantsPage + 1"
      :serverTotalElements="participantsTotalElements"
      :participantsLoading="participantsLoading"
      @page-change="onParticipantsPageChange"
      @search-change="onParticipantsSearchChange"
      @update-tournament-data="updateTournamentData"
      @background-color-change="handleBackgroundColorChange"/>

    <!-- Check if this should render in minimal mode (skip for admins who need full controls) -->
    <template v-else-if="showMinimalView && !adminUser">
      <!-- Minimal View - Only show header, timer and leaderboard -->
      <v-col class="tournament-embed-header px-0">
        <!-- Desktop header layout -->
        <div class="d-none d-sm-block">
          <!-- Show tournament organizer logo when available -->
          <div v-if="$store.state.tournament.imageUrl" class="organizer-logo-container text-center mb-4">
            <img :src="$store.state.tournament.imageUrl" alt="Tournament Organizer" class="organizer-logo" />
          </div>

          <span :class="['status-chip', tournamentStatus.toLowerCase().replace(' ', '-')]" :style="statusChipStyle">
            {{ tournamentStatus.charAt(0).toUpperCase() + tournamentStatus.slice(1).toLowerCase() }}
          </span>
          <div class="d-flex align-center">
            <h1 class="tournament-title mt-4">{{ $store.state.tournament.tournamentName }}</h1>
          </div>
          <h3 class="tournament-organizer">
            {{ $t('header.creatorPrepend') }} {{ $store.state.tournament.tournamentOrganizer }}
          </h3>
        </div>

        <!-- Enhanced Mobile header layout -->
        <div class="mobile-tournament-header d-block d-sm-none">
          <!-- Show tournament organizer logo when available (mobile) -->
          <div v-if="$store.state.tournament.imageUrl" class="organizer-logo-container text-center mb-3">
            <img :src="$store.state.tournament.imageUrl" alt="Tournament Organizer" class="organizer-logo" />
          </div>

          <!-- Tournament title with visual enhancement -->
          <div class="mobile-tournament-title-container">
            <h1 class="mobile-tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
            <div class="mobile-title-underline"></div>
          </div>

          <!-- Organizer info -->
          <h3 class="mobile-tournament-organizer">
            {{ $t('header.creatorPrepend') }} <span class="organizer-name">{{
              $store.state.tournament.tournamentOrganizer }}</span>
          </h3>
        </div>

        <!-- Tournament Timer - positioned better for both desktop and mobile -->
        <v-col v-if="shouldShowTimer" cols="12" class="tournament-timer-container">
          <TournamentTimer :tournamentStartDate="new Date($store.state.tournament.startTime)"
            :tournamentEndDate="new Date($store.state.tournament.endTime)"
            :tournamentInProgress="tournamentStatus === 'IN PROGRESS'"
            :tournamentIsActive="tournamentStatus === 'STILL OPEN' || tournamentStatus === 'IN PROGRESS'"
            :tournamentUserEnrolled="meAsParticipant" />
        </v-col>
      </v-col>

      <!-- Show only the leaderboard -->
      <v-row class="mx-0">
        <v-col cols="12">
          <TournamentStopped v-if="stoppedTournament" />
          <Leaderboard :key="participantsCurrentId"
            :currentParticipant="meAsParticipant"
            v-else-if="effectiveAdminMode || ((started || finished) && !inPostRegistrationWindow)" :adminMode="effectiveAdminMode"
            :serverPagination="true"
            :serverTotalPages="participantsTotalPages"
            :serverCurrentPage="participantsPage + 1"
            :serverTotalElements="participantsTotalElements"
            :participantsLoading="participantsLoading"
            @page-change="onParticipantsPageChange"
            @search-change="onParticipantsSearchChange" />
          <TournamentPostStartRegistration v-else-if="inPostRegistrationWindow"
            :i-am-participant="meAsParticipant !== null"
            :days-post-register="$store.state.tournament.tournamentSettings.daysPostRegister"
            :tournament-start-time="$store.state.tournament.startTime" />
          <TournamentNotStarted v-else :inviteOnly="inviteOnly" @goToParticipants="overlayTournamentParticipants = true"
            :i-am-participant="meAsParticipant !== null" />
        </v-col>
      </v-row>

      <!-- Add buttons with organizer color for minimal view too -->
      <div class="d-flex mt-4 justify-center">
        <button v-if="shouldShowRules" class="desktop-action-btn" @click="overlayTournamentRules = true"
          :style="organizerColorStyle">
          <i class="fa-regular fa-list mr-2"></i>
          {{ $t('viewRules') }}
        </button>
        <button v-if="shouldShowFaqs && $store.state.tournament.faqsUrl" class="desktop-action-btn ml-3"
          @click="goToFaqs" :style="organizerColorStyle">
          <i class="fa-regular fa-circle-question mr-2"></i>
          {{ $t('viewFAQs') }}
        </button>
      </div>
    </template>

    <!-- Regular view for logged-in users - Using new improved layout -->
    <template v-else>
      <!-- Basic tournament header with different mobile presentation -->
      <v-col class="tournament-embed-header px-0" :class="{ 'is-finished': finished }">
        <!-- Desktop header layout with admin controls -->
        <div class="d-none d-sm-block">
          <!-- Top controls bar (Figma): back link for everyone; help/rules icons; admin tools -->
          <div class="admin-controls-bar mb-4">
            <div class="admin-controls-left">
              <button class="admin-breadcrumb" @click="goBackToCompetitions">
                <i class="fa-solid fa-chevron-left mr-2"></i>
                {{ $t('goBackToCompetitions') || 'Go back to competitions' }}
              </button>
            </div>
            <div class="admin-controls-right">
              <button v-if="shouldShowFaqs && (adminMode || $store.state.tournament?.faqsUrl)" class="admin-icon-btn" @click="$store.state.tournament?.faqsUrl ? goToFaqs() : showNoFaqsMessage()" :title="$t('viewFAQs') || 'FAQs'">
                <i class="fa-regular fa-circle-question"></i>
              </button>
              <button v-if="shouldShowRules" class="admin-icon-btn" @click="overlayTournamentRules = true" :title="$t('viewRules') || 'Rules'">
                <i class="fa-regular fa-list-ul"></i>
              </button>
              <template v-if="adminUser && adminMode">
                <span class="admin-divider"></span>
                <button class="admin-icon-btn" :disabled="new Date() > new Date($store.state.tournament.endTime)" @click="goToTournamentSettings" :title="$t('settings') || 'Settings'">
                  <i class="fa-regular fa-gear"></i>
                </button>
                <button class="admin-icon-btn" @click="overlayTournamentShare = true" :title="$t('share') || 'Share'">
                  <i class="fa-regular fa-share-nodes"></i>
                </button>
              </template>
              <button v-if="adminUser" class="admin-view-toggle" @click="adminMode = !adminMode">
                {{ adminMode ? $t('viewAsUser') || 'View as User' : $t('viewAsAdmin') }}
                <i class="fa-regular fa-user-crown ml-2"></i>
              </button>
            </div>
          </div>

          <!-- REVIEWING: 24h finalize window (deadline anchored to competition end — TD-012) -->
          <div
            v-if="showReviewingFinalizeBanner"
            id="finalize-competition-banner"
            class="finalize-competition-banner"
            :style="finalizeBannerStyle"
          >
            <div class="finalize-competition-banner__main">
              <h2 class="finalize-competition-banner__title">{{ $t('finalizeCompetitionBanner.title') }}</h2>
              <p class="finalize-competition-banner__desc">
                {{ $t('finalizeCompetitionBanner.description') }}
              </p>
              <button
                type="button"
                class="finalize-competition-banner__cta"
                @click="confirmManualClosure"
              >
                {{ $t('finalizeCompetitionBanner.closeButton') }}
                <i class="fa-solid fa-check ml-2" aria-hidden="true"></i>
              </button>
            </div>
            <div class="finalize-competition-banner__countdown" aria-live="polite">
              <div class="finalize-competition-banner__unit">
                <span class="finalize-competition-banner__num">{{ reviewingCountdownParts.days }}</span>
                <span class="finalize-competition-banner__label">{{ $t('finalizeCompetitionBanner.dayShort') }}</span>
              </div>
              <div class="finalize-competition-banner__unit">
                <span class="finalize-competition-banner__num">{{ reviewingCountdownParts.hours }}</span>
                <span class="finalize-competition-banner__label">{{ $t('finalizeCompetitionBanner.hourShort') }}</span>
              </div>
              <div class="finalize-competition-banner__unit">
                <span class="finalize-competition-banner__num">{{ reviewingCountdownParts.minutes }}</span>
                <span class="finalize-competition-banner__label">{{ $t('finalizeCompetitionBanner.minShort') }}</span>
              </div>
              <div class="finalize-competition-banner__unit">
                <span class="finalize-competition-banner__num">{{ reviewingCountdownParts.seconds }}</span>
                <span class="finalize-competition-banner__label">{{ $t('finalizeCompetitionBanner.secShort') }}</span>
              </div>
            </div>
          </div>

          <!-- Info bar (Figma): title + status + organizer on the left, countdown timer on the right -->
          <div class="header-info-bar">
            <div class="header-info-bar__left">
              <div class="d-flex align-center">
                <h1 class="tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
                <span :class="['status-chip', 'ml-3', tournamentStatus.toLowerCase().replace(' ', '-')]" :style="statusChipStyle">
                  {{ tournamentStatus.charAt(0).toUpperCase() + tournamentStatus.slice(1).toLowerCase() }}
                </span>
              </div>
              <h3 class="tournament-organizer">
                {{ $t('header.creatorPrepend') }} <span class="tournament-organizer__name">{{ $store.state.tournament.tournamentOrganizer }}</span>
              </h3>
              <div v-if="showEndedTimerLabel" class="tournament-ended-label-overview">
                <TournamentEndedLabel
                  :tournament-end-date="new Date($store.state.tournament.endTime)"
                />
              </div>
            </div>
            <div v-if="shouldShowTimer && !showEndedTimerLabel" class="header-info-bar__timer">
              <TournamentTimer :tournamentStartDate="new Date($store.state.tournament.startTime)"
                :tournamentEndDate="new Date($store.state.tournament.endTime)"
                :tournamentInProgress="tournamentStatus === 'IN PROGRESS'"
                :tournamentIsActive="tournamentStatus === 'STILL OPEN' || tournamentStatus === 'IN PROGRESS'"
                :tournamentUserEnrolled="meAsParticipant" />
            </div>
          </div>

          <!-- Registration banner (Figma): partner-colored strip with countdown + Register Now -->
          <div v-if="showRegistrationBanner" class="registration-banner" :style="registrationBannerStyle">
            <div class="registration-banner__texts">
              <p class="registration-banner__title" v-html="$t('registrationBanner.title', { date: `<b>${registrationDeadlineLabel}</b>` })"></p>
              <p class="registration-banner__countdown">
                {{ $t('registrationBanner.closesIn', registrationCountdownParts) }}
              </p>
            </div>
            <button class="registration-banner__cta" :style="registrationBannerCtaStyle" @click="scrollToRegistration">
              {{ $t('registerNow') }}
              <i class="fa-regular fa-user-plus ml-2"></i>
            </button>
          </div>
        </div>

        <!-- Enhanced Mobile header layout -->
        <div class="mobile-tournament-header d-block d-sm-none">
          <!-- Tournament title with visual enhancement -->
          <div class="mobile-tournament-title-container">
            <h1 class="mobile-tournament-title">{{ $store.state.tournament.tournamentName }}</h1>
            <div class="mobile-title-underline"></div>
          </div>

          <!-- Organizer info -->
          <h3 class="mobile-tournament-organizer">
            {{ $t('header.creatorPrepend') }} <span class="organizer-name">{{
              $store.state.tournament.tournamentOrganizer }}</span>
          </h3>

          <!-- Mobile admin controls -->
          <div v-if="adminUser" class="mobile-admin-controls">
            <v-btn icon @click="adminMode = !adminMode">
              <v-icon>{{ adminMode ? 'fa-regular fa-user' : 'fa-cog' }}</v-icon>
            </v-btn>
            <v-btn v-if="adminMode" icon class="ml-2" @click="tournamentSettings">
              <v-icon>fa-cog</v-icon>
            </v-btn>
            <v-btn v-if="adminMode" icon class="ml-2" @click="tournamentParticipants">
              <v-icon>fa-users</v-icon>
            </v-btn>
          </div>
        </div>



        <!-- Tournament Timer (mobile only — desktop timer lives in the header info bar) -->
        <v-col
          v-if="shouldShowTimer"
          cols="12"
          class="tournament-timer-container d-block d-sm-none"
        >
          <TournamentTimer :tournamentStartDate="new Date($store.state.tournament.startTime)"
            :tournamentEndDate="new Date($store.state.tournament.endTime)"
            :tournamentInProgress="tournamentStatus === 'IN PROGRESS'"
            :tournamentIsActive="tournamentStatus === 'STILL OPEN' || tournamentStatus === 'IN PROGRESS'"
            :tournamentUserEnrolled="meAsParticipant" />
        </v-col>
      </v-col>

      <!-- Ended admin block: tabs always precede summary (flex column + stable spacing
           after header timer margins). -->
      <div v-if="isClubAdmin && finished && effectiveAdminMode" class="ended-admin-section">
        <div class="ended-view-toggle">
          <div class="ended-view-tabs tabs" :style="endedTabsStyle">
            <input type="radio" id="ended-overview-tab" name="ended-tabs" value="overview" v-model="endedSummaryView" />
            <label class="tab" for="ended-overview-tab">
              <i class="fa-regular fa-chart-pie"></i>{{ $t('overview') }}
            </label>
            <input type="radio" id="ended-members-tab" name="ended-tabs" value="members" v-model="endedSummaryView" />
            <label class="tab" for="ended-members-tab">
              <i class="fa-regular fa-users"></i>{{ $t('members') }}
            </label>
            <span class="glider"></span>
          </div>
        </div>

        <div v-if="endedSummaryView === 'overview'" class="ended-admin-section__summary">
          <TournamentEndedSummary :tournament-id="$store.state.tournament && $store.state.tournament.tournamentId" />
        </div>
      </div>

      <!-- Main content area with improved layout -->
      <v-row v-if="!effectiveAdminMode || !isClubAdmin || !finished || endedSummaryView === 'members'">
        <!-- Mobile User Summary - Only shown on mobile -->
        <v-col cols="12" class="show-on-mobile mb-4">
          <div class="mobile-leaderboard-header d-block d-sm-none" style="margin-bottom: 15px;">
            <div class="leaderboard-title-container">
              <i class="fa-regular fa-user mr-2"></i>
              <h2>{{ $t('yourParticipation') }}</h2>
            </div>
          </div>
          <UserSummary v-if="meAsParticipant && !showSkinReveal" :canChangeAcc="!started && !finished && !stoppedTournament"
            :participants="$store.state.tournament.participants" :participant-data="meAsParticipant"
            :ranking-type="$store.state.tournament.tournamentSettings.tournamentRankingType"
            :tournament-closed="tournamentStatus === 'CLOSED'" />
          <div v-else class="no-participant-message">
            <p>{{ $t('notParticipant') }}</p>
          </div>
        </v-col>

        <!-- Tournament content with two column layout -->
          <!-- Left column - Tournament main content (leaderboard or other state component) -->
          <v-col ref="mainContentCol" :cols="12" :lg="(effectiveAdminMode || !hasRightColumnContent) ? 12 : 8" :md="(effectiveAdminMode || !hasRightColumnContent) ? 12 : 8" :sm="12" :xs="12" class="px-0">
            <!-- Tournament Brackets -->
            <TournamentBrackets
              v-if="$store.state.tournament && $store.state.tournament.tournamentId"
              :tournament-id="$store.state.tournament.tournamentId"
              @bracket-selected="handleBracketChange" />

            <TournamentStopped v-if="stoppedTournament" />
            <template v-else-if="effectiveAdminMode || ((started || finished) && !inPostRegistrationWindow)">
              <!-- Keying on participantsCurrentId forces a full Leaderboard + Table
                   remount when the user navigates between tournaments. Without it,
                   Table.cachedPodiumItems would hold the previous tournament's top 3. -->
              <Leaderboard :key="participantsCurrentId"
                :currentParticipant="meAsParticipant" :adminMode="effectiveAdminMode"
                :tournamentEnded="new Date($store.state.tournament.endTime) <= new Date()"
                :serverPagination="true"
                :serverTotalPages="participantsTotalPages"
                :serverCurrentPage="participantsPage + 1"
                :serverTotalElements="participantsTotalElements"
                :participantsLoading="participantsLoading"
                @page-change="onParticipantsPageChange"
                @search-change="onParticipantsSearchChange"
                @disqualify="onDisqualifyParticipant"
                @activate="onActivateParticipant"
                @see-journal="onSeeJournal"
                @send-email="sendAccountEmail"
                @download-members="sendParticipantsEmail"
                @add-participant="participantsInitialView = 'ADD'; overlayTournamentParticipants = true" />
            </template>
            <TournamentPostStartRegistration v-else-if="inPostRegistrationWindow"
              :i-am-participant="meAsParticipant !== null"
              :days-post-register="$store.state.tournament.tournamentSettings.daysPostRegister"
              :tournament-start-time="$store.state.tournament.startTime" />
            <TournamentNotStarted v-else :inviteOnly="inviteOnly"
              @goToParticipants="overlayTournamentParticipants = true" :i-am-participant="meAsParticipant !== null" />
          </v-col>

          <!-- Right column - User summary and tournament details (only visible in non-admin mode).
               We keep it mounted but hidden when empty so TournamentPrizePool can still fetch & emit
               its own visibility (otherwise we'd never know if a prize pool exists). -->
          <v-col v-if="!effectiveAdminMode" v-show="hasRightColumnContent" cols="12" lg="4" md="4" sm="12" xs="12" class="d-flex flex-column">
            <!-- Desktop User Summary (hidden on mobile) -->
            <UserSummary v-if="meAsParticipant && !showSkinReveal" class="hide-on-mobile" :canChangeAcc="!started && !finished && !stoppedTournament"
              :participants="$store.state.tournament.participants" :participant-data="meAsParticipant"
              :ranking-type="$store.state.tournament.tournamentSettings.tournamentRankingType"
              :tournament-closed="tournamentStatus === 'CLOSED'" />

            <!-- Tournament Details Header - Mobile Only -->
            <div class="mobile-leaderboard-header d-block d-sm-none">
              <div class="leaderboard-title-container">
                <i class="fa-regular fa-trophy mr-2"></i>
                <h2>{{ $t('tournamentDetails') }}</h2>
              </div>
            </div>

            <!-- Tournament Awards -->
            <div class="mt-5">
              <TournamentAwards :should-show-awards="shouldShowAwards" :should-show-sponsor="shouldShowSponsor"  />
            </div>

            <!-- Tournament Prize Pool -->
            <div class="mt-5">
              <TournamentPrizePool @visibility-change="rightColumnPrizePoolVisible = $event" />
            </div>

            <!-- Desktop Rules/FAQs moved to the top controls bar icons (Figma redesign) -->
          </v-col>
      </v-row>

      <!-- Static Mobile Footer with Rules and FAQs (non-fixed) -->
      <div class="mobile-embed-footer d-block d-sm-none">
        <div class="footer-actions">
          <button v-if="shouldShowRules" class="footer-action-btn" @click="overlayTournamentRules = true" :style="organizerColorStyle">
            <i class="fa-regular fa-list mr-2"></i>
            {{ $t('viewRules') }}
          </button>
          <button v-if="shouldShowFaqs && $store.state.tournament.faqsUrl" class="footer-action-btn" @click="goToFaqs"
            :style="organizerColorStyle">
            <i class="fa-regular fa-circle-question mr-2"></i>
            {{ $t('viewFAQs') }}
          </button>
        </div>
      </div>
    </template>

    <SkinRevealDialog
      :visible="showSkinReveal"
      :participant-data="meAsParticipantSkinned || {}"
      :theme-code="$store.state.tournament && $store.state.tournament.tournamentSettings && $store.state.tournament.tournamentSettings.skinTheme ? $store.state.tournament.tournamentSettings.skinTheme.code : ($store.state.tournament && $store.state.tournament.competitionSkinTheme ? $store.state.tournament.competitionSkinTheme.code : null)"
      :category-display-name="$store.state.tournament && $store.state.tournament.tournamentSettings && $store.state.tournament.tournamentSettings.skinTheme ? $store.state.tournament.tournamentSettings.skinTheme.displayName : ($store.state.tournament && $store.state.tournament.competitionSkinTheme ? $store.state.tournament.competitionSkinTheme.displayName : '')"
      @close="dismissSkinReveal"
    />

    <!-- All overlays kept from original component -->
    <v-overlay :dark="false" v-model="overlayTradesDistribution">
      <TradesDistribution :broker-account="(meAsParticipant !== null) ? meAsParticipant.brokerAccountId : 0"
        :from-date="$store.state.tournament.startTime" :faqs-url="$store.state.tournament.faqsUrl"
        :to-date="$store.state.tournament.endTime"
        :paused="(meAsParticipant !== null) ? meAsParticipant.status === 'PAUSED' : 'PAUSED'"
        @closeWindow="overlayTradesDistribution = false" />
    </v-overlay>

    <!-- <v-overlay :dark="false" v-model="overlayTournamentSettings">
      <TournamentSettings :tournament-summary="$store.state.tournament"
        @closeWindow="overlayTournamentSettings = false" />
    </v-overlay> -->

    <v-overlay :dark="false" v-model="overlayTournamentParticipants">
      <TournamentParticipants :tournament-summary="$store.state.tournament"
        :initial-view="participantsInitialView"
        @participantsChanged="onOverlayParticipantsChanged"
        @closeWindow="overlayTournamentParticipants = false; participantsInitialView = 'GENERAL'" />
    </v-overlay>

    <v-navigation-drawer
      v-model="overlayTournamentShare"
      right
      temporary
      fixed
      hide-overlay
      width="540"
      class="share-drawer"
    >
      <TournamentShare :tournament-id="$store.state.tournament.tournamentId"
        :externalId="$store.state.tournament.tournamentSettings.externalId"
        @closeWindow="overlayTournamentShare = false" @copied="showCopiedSnackbar" @emailSent="handleEmailSent"
        @emailError="handleEmailError" :tournamentStatus="tournamentStatus"
        @participantsChanged="onOverlayParticipantsChanged" />
    </v-navigation-drawer>

    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('close_TournamentOverview') }}
        </v-btn>
      </template>
    </v-snackbar>

    <v-navigation-drawer
      v-model="overlayTournamentRules"
      right
      temporary
      fixed
      hide-overlay
      width="460"
      class="rules-drawer"
    >
      <RulesDialog :rules="formattedRulesHtml" :admin-mode="effectiveAdminMode" @close="overlayTournamentRules = false" />
    </v-navigation-drawer>

    <!-- Disqualify/Activate Confirmation Dialog -->
    <v-dialog v-model="showDisqualifyDialog" max-width="520px" persistent>
      <v-card class="disqualify-confirm-dialog">
        <v-card-title class="d-flex align-center">
          <v-avatar size="50" class="mr-3">
            <v-img :src="disqualifyTarget && disqualifyTarget.profilePicture ? disqualifyTarget.profilePicture : 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'" />
          </v-avatar>
          <span style="font-family: 'Aeonik', 'Avenir', sans-serif;">
            {{ disqualifyAction === 'ACTIVATE'
              ? ($t('confirmActivate') || 'Activate Participant')
              : isRemoveSelected ? $t('confirmRemove') : ($t('confirmDisqualify') || 'Disqualify Participant')
            }}
          </span>
        </v-card-title>
        <v-card-text v-if="disqualifyTarget" style="font-family: 'Avenir', sans-serif;">
          <!-- SFB-1205: before the competition starts the same member action offers
               Disqualify (reversible) and Remove (permanent) side by side. -->
          <v-radio-group
            v-if="disqualifyAction === 'DISQUALIFY' && competitionNotStarted"
            v-model="memberActionChoice"
            class="mt-0 member-action-choice"
            hide-details
          >
            <v-radio value="DISQUALIFY" color="mainGreen">
              <template v-slot:label>
                <div>
                  <strong>{{ $t('memberActionDisqualify') }}</strong>
                  <div class="member-action-hint">{{ $t('memberActionDisqualifyHint') }}</div>
                </div>
              </template>
            </v-radio>
            <v-radio value="REMOVE" color="error" class="mt-2">
              <template v-slot:label>
                <div>
                  <strong>{{ $t('memberActionRemove') }}</strong>
                  <div class="member-action-hint">{{ $t('memberActionRemoveHint') }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
          <p :class="{ 'mt-4': disqualifyAction === 'DISQUALIFY' && competitionNotStarted }">{{ disqualifyAction === 'ACTIVATE'
            ? ($t('confirmActivateDesc', { name: disqualifyTarget.firstName }) || `Are you sure you want to re-activate ${disqualifyTarget.firstName}?`)
            : isRemoveSelected
              ? $t('confirmRemoveDesc', { name: disqualifyTarget.firstName })
              : ($t('confirmDisqualifyDesc', { name: disqualifyTarget.firstName }) || `Are you sure you want to disqualify ${disqualifyTarget.firstName}?`)
          }}</p>
          <p v-if="isRemoveSelected" class="remove-caution error--text font-weight-bold">
            {{ $t('removeCautionIrreversible') }}
          </p>
          <v-textarea
            v-if="disqualifyAction === 'DISQUALIFY'"
            v-model="disqualificationReasonInput"
            outlined
            dense
            rows="3"
            auto-grow
            maxlength="35"
            :counter="35"
            class="mt-3"
            :label="isRemoveSelected
              ? $t('removalReasonOptional')
              : ($t('disqualificationReasonOptional') || 'Reason for disqualification (optional)')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="tournament-dialog-cancel-btn" @click="showDisqualifyDialog = false">{{ $t('btnCancel') || 'Cancel' }}</v-btn>
          <v-btn :color="disqualifyAction === 'DISQUALIFY' ? 'error' : 'success'" :loading="processingAction" @click="executeStatusChange">
            {{ disqualifyAction === 'ACTIVATE'
              ? ($t('btnActivate') || 'Activate')
              : isRemoveSelected ? $t('btnRemoveParticipant') : ($t('btnDisqualify') || 'Disqualify')
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Resend account / welcome email (admin) -->
    <v-dialog
      v-model="showSendAccountEmailDialog"
      max-width="480px"
      persistent
      @input="onSendAccountEmailDialogToggle"
    >
      <v-card class="disqualify-confirm-dialog">
        <v-card-title class="d-flex align-center">
          <v-avatar size="50" class="mr-3">
            <v-img :src="sendEmailTarget && sendEmailTarget.profilePicture ? sendEmailTarget.profilePicture : 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png'" />
          </v-avatar>
          <span style="font-family: 'Aeonik', 'Avenir', sans-serif;">
            {{ $t('sendAccountEmailTitle') || 'Send email to participant' }}
          </span>
        </v-card-title>
        <v-card-text v-if="sendEmailTarget" style="font-family: 'Avenir', sans-serif;">
          <p class="mb-4">
            {{ $t('sendAccountEmailDesc', { name: sendEmailTarget.firstName || sendEmailTarget.username || '' }) || `Choose which message to send to ${sendEmailTarget.firstName || sendEmailTarget.username || 'this participant'}.` }}
          </p>
          <v-radio-group v-model="sendEmailType" column hide-details class="mt-0 send-email-type-radios">
            <v-radio
              :ripple="false"
              :label="$t('sendAccountEmailTypeCredentials') || 'Account credentials'"
              value="CREDENTIALS"
            />
            <v-radio
              :ripple="false"
              :label="$t('sendAccountEmailTypeWelcome') || 'Public ranking'"
              value="WELCOME"
            />
            <v-radio
              :ripple="false"
              :label="$t('sendAccountEmailTypeAll') || 'Both (account credentials and public ranking)'"
              value="ALL"
            />
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn depressed class="tournament-send-email-cancel-btn" @click="showSendAccountEmailDialog = false">
            {{ $t('btnCancel') || 'Cancel' }}
          </v-btn>
          <v-btn color="primary" :loading="processingSendEmail" :disabled="!sendEmailTarget || sendEmailTarget.participantId == null" @click="executeSendAccountEmail">
            {{ $t('sendAccountEmailConfirm') || 'Send email' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Resend email — success confirmation -->
    <v-dialog v-model="showSendAccountEmailSuccessDialog" max-width="480px">
      <v-card class="disqualify-confirm-dialog">
        <v-card-title class="d-flex align-center">
          <v-icon color="success" class="mr-3" size="36">
            fa-check-circle
          </v-icon>
          <span style="font-family: 'Aeonik', 'Avenir', sans-serif;">
            {{ $t('sendAccountEmailSuccessDialogTitle') }}
          </span>
        </v-card-title>
        <v-card-text v-if="sendAccountEmailSuccessInfo" style="font-family: 'Avenir', sans-serif;">
          <p v-if="sendAccountEmailSuccessInfo.message" class="mb-2">
            {{ sendAccountEmailSuccessInfo.message }}
          </p>
          <p
            v-if="sendAccountEmailSuccessInfo.credentialsMessage"
            class="mb-2 body-2 grey--text text--darken-1"
          >
            {{ sendAccountEmailSuccessInfo.credentialsMessage }}
          </p>
          <p
            v-if="sendAccountEmailSuccessInfo.welcomeMessage"
            class="mb-2 body-2 grey--text text--darken-1"
          >
            {{ sendAccountEmailSuccessInfo.welcomeMessage }}
          </p>
          <p v-if="sendAccountEmailSuccessInfo.account" class="mb-0 mt-3">
            <span class="font-weight-medium">{{ $t('sendAccountEmailSuccessAccountLabel') }}</span>
            {{ sendAccountEmailSuccessInfo.account }}
          </p>
          <p v-if="sendAccountEmailSuccessInfo.mail" class="mb-0 mt-2 caption grey--text">
            {{ $t('sendAccountEmailSuccessParticipantLabel', { mail: sendAccountEmailSuccessInfo.mail }) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="closeSendAccountEmailSuccessDialog">
            {{ $t('btnOk') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Manual closure: styles live unscoped below — v-dialog attaches outside scoped subtree. Accent = tournament primary (same chain as leaderboard). -->
    <v-dialog v-model="showClosureConfirmation" max-width="480px" persistent content-class="manual-closure-dialog-wrap">
      <v-card class="manual-closure-dialog" elevation="0" rounded="lg" :style="manualClosureDialogThemeStyle">
        <div class="manual-closure-dialog__inner">
          <div class="manual-closure-dialog__header">
            <div class="manual-closure-dialog__title">{{ $t('confirmManualClosure.title') }}</div>
            <button
              type="button"
              class="manual-closure-dialog__close"
              :aria-label="$t('close')"
              @click="showClosureConfirmation = false"
            >
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
          <v-divider class="manual-closure-dialog__divider"></v-divider>
          <div class="manual-closure-dialog__body">
            <p class="manual-closure-dialog__message">
              {{ $t('confirmManualClosure.message') }}
              
            </p>
            <p class="manual-closure-dialog__emphasis">{{ $t('confirmManualClosure.warning') }}</p>
          </div>
        </div>
        <v-card-actions class="manual-closure-dialog__actions pa-6 pt-2">
          <v-btn
            depressed
            rounded
            class="manual-closure-dialog__action-btn manual-closure-dialog__action-btn--cancel"
            @click="showClosureConfirmation = false"
          >
            {{ $t('btnCancel') }}
          </v-btn>
          <v-btn
            depressed
            rounded
            class="manual-closure-dialog__action-btn manual-closure-dialog__action-btn--confirm"
            :loading="closingTournament"
            @click="manuallyCloseTournament"
          >
            {{ $t('confirmManualClosure.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="exportLoading" persistent max-width="320">
      <v-card class="pa-6 text-center">
        <v-progress-circular
          indeterminate
          size="48"
          width="4"
          color="primary"
          class="mb-4"
        />
        <div class="text-h6 mb-2">{{ $t('downloadingFile') }}</div>
        <div class="text-body-2">{{ $t('preparingCsv') }}</div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { setLocale, i18nEventBus } from '@/i18n';
import Leaderboard from "@/views/Tournament/components/Leaderboard";
import Repository from '../../../repositories/tournament';
import { isSkinEnabled, buildSkinFromServer, getThemeCode } from '@/services/skins.service';
import { escapeHtml } from '@/utils/safeHtml';
import UserSummary from "@/views/Tournament/components/UserSummary";
import SkinRevealDialog from "@/components/SkinRevealDialog.vue";
import TournamentTimer from "@/components/TournamentTimer";
import TournamentEndedLabel from "@/components/TournamentEndedLabel.vue";
import TradesDistribution from "@/views/Tournament/components/TradesDistribution";
import TournamentParticipants from "@/views/Tournament/components/TournamentParticipants";
import TournamentNotStarted from "@/views/Tournament/components/TournamentNotStarted.vue";
import TournamentPostStartRegistration from "@/views/Tournament/components/TournamentPostStartRegistration.vue";
import TournamentAwards from "@/views/Tournament/components/TournamentAwards";
import TournamentPrizePool from "@/views/Tournament/components/TournamentPrizePool";
import TournamentStopped from "@/views/Tournament/components/TournamentStopped.vue";
import TournamentBrackets from "@/views/Tournament/components/TournamentBrackets";
import TournamentShare from "@/views/Tournament/components/TournamentShare";
import TournamentEmbedView from "@/views/Tournament/components/TournamentEmbedView";
import RulesDialog from "@/views/Tournament/components/RulesDialog.vue";
import TournamentEndedSummary from "@/views/Tournament/components/TournamentEndedSummary";

/** Hours admins have to close after COMPETITION end before auto-close — must match UX copy */
const FINALIZE_REVIEW_WINDOW_HOURS = 24;
const FINALIZE_REVIEW_WINDOW_MS = FINALIZE_REVIEW_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * SFB-1169 registration banner ("You have until X to secure your spot…").
 * Implemented per the Figma redesign but held back pending Product confirmation —
 * flip to true to enable. All banner logic/markup/i18n stays in place.
 */
const REGISTRATION_BANNER_ENABLED = false;

function padTwo(n) {
  return String(Math.max(0, n)).padStart(2, '0');
}

/** Same resolution order as tournament-pos-table `adminHeaderStyle` (primary → club → default). */
function resolveAdminBannerHexClean(rawPrimary) {
  let h = String(rawPrimary ?? '').replace('#', '').trim();
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) {
    return null;
  }
  return h;
}

export default {
  name: "TournamentOverview",
  components: {
    TournamentStopped,
    TournamentAwards,
    TournamentPrizePool,
    TournamentNotStarted,
    TournamentPostStartRegistration,
    TournamentParticipants,
    TradesDistribution,
    TournamentTimer,
    TournamentEndedLabel,
    UserSummary,
    Leaderboard,
    TournamentShare,
    TournamentEmbedView,
    RulesDialog,
    TournamentBrackets,
    TournamentEndedSummary,
    SkinRevealDialog,
  },
  props: {
    tournamentId: Number,
    /** Set by route wrappers (TournamentOverviewWrapper / CompetitionOverview) because in-component guards do not run on nested mounts. */
    navigationReferrer: {
      type: String,
      default: null,
    },
  },
  watch: {
    "$store.state.tournament": {
      deep: true,
      immediate: true,
      handler() {
        this.verifyMeAsParticipant();
        this.getTournamentRules();
        this.maybeShowSkinReveal();
      }
    },
    // Watch for query param changes to handle navigation between tournaments
    "$route.query.tournamentId": {
      handler(newId, oldId) {
        // Only reload if the tournament ID actually changed and we have a new ID
        if (newId && newId !== oldId) {
          this.init();
        }
      }
    },
    // Toggling "View as User" / "View as Admin" switches the source endpoint
    // (public-by-externalId vs admin), so refetch page 0 with the current
    // search term. Skip when no tournament is loaded yet, when we're in the
    // embed view (always public), when legacy/skin mode is active (the
    // legacy overview endpoint serves both modes — no swap), or when
    // there's no externalId (admin endpoint is the only source either way).
    adminMode() {
      this.checkPostRegistrationWindow();
      if (!this.participantsCurrentId) return;
      if (this.isEmbedView) return;
      if (this.useLegacyOverview) return;
      const externalId = this.$store.state.tournament?.tournamentSettings?.externalId;
      if (!externalId) return;
      this.participantsPage = 0;
      this.fetchParticipants(0, this.participantsSearch);
    },
    overlayTournamentRules(open) {
      // Hide/show external chat widget (rendered in iframe, CSS can't reach it)
      const chatElements = document.querySelectorAll('#hubspot-messages-iframe-container, iframe[id*="chat"], iframe[id*="hotline"], [class*="d-hotline"], #drift-widget, #intercom-container, #launcher, .intercom-lightweight-app');
      chatElements.forEach(el => {
        el.style.display = open ? 'none' : '';
      });
      // Also try to find any iframe containing the chat widget
      document.querySelectorAll('iframe').forEach(iframe => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc && iframeDoc.querySelector('.d-hotline')) {
            iframe.style.display = open ? 'none' : '';
          }
        } catch (e) {
          // Cross-origin iframe, check by attributes
          if (iframe.className.includes('hotline') || iframe.id.includes('hotline') || iframe.src.includes('hotline') || iframe.src.includes('desku') || iframe.src.includes('chat')) {
            iframe.style.display = open ? 'none' : '';
          }
        }
      });
    },
    // Watch for prop changes (used by broker app's CompetitionOverview)
    tournamentId: {
      handler(newId, oldId) {
        // Only reload if the tournament ID prop changed and we have a new ID
        if (newId && newId !== oldId) {
          console.log('Tournament ID prop changed from', oldId, 'to', newId);
          this.init();
        }
      }
    },
    showReviewingFinalizeBanner: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.startReviewingCountdownTicker();
        } else {
          this.stopReviewingCountdownTicker();
        }
      },
    },
    showRegistrationBanner: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.startRegistrationCountdownTicker();
        } else {
          this.stopRegistrationCountdownTicker();
        }
      },
    },
  },
  data() {
    return {
      overlayProcessing: false,
      started: false,
      finished: false,
      inPostRegistrationWindow: false,
      secsAutoRefresh: 0,
      adminUser: false,
      meAsParticipant: null,
      overlayTradesDistribution: false,
      overlayTournamentSettings: false,
      overlayTournamentParticipants: false,
      participantsInitialView: 'GENERAL',
      overlayTournamentShare: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'mainGreen',
      isMobile: false,
      // Synced to `adminUser` in verifyIfAdmin(); false default keeps the
      // pre-load render in user view until the tournament/role is resolved.
      adminMode: false,
      tournamentRules: undefined,
      overlayTournamentRules: false,
      // Track current loading request to prevent race conditions
      currentLoadingTournamentId: null,
      isEmbedView: false,
      currentLocale: null, // Add this new property to track language changes
      refreshKey: 0, // Add this to force component re-render
      tournamentScheduleInterval: null,
      showClosureConfirmation: false,
      closingTournament: false,
      showDisqualifyDialog: false,
      disqualifyTarget: null,
      disqualifyAction: 'DISQUALIFY',
      disqualificationReasonInput: '',
      // SFB-1205: inside the member action popup the admin picks between
      // disqualifying (reversible) and removing (permanent, pre-start only).
      memberActionChoice: 'DISQUALIFY',
      processingAction: false,
      embedBackgroundColor: '',
      // Ended tournament summary view toggle ('overview' | 'members')
      endedSummaryView: 'overview',
      // Right column content flags — updated via child emits for async-loaded data
      rightColumnPrizePoolVisible: false,
      // Server-side participants pagination state
      participantsPage: 0,
      participantsTotalPages: 1,
      participantsTotalElements: 0,
      participantsLoading: false,
      participantsSearch: '',
      participantsCurrentId: null,
      exportLoading: false,
      showSendAccountEmailDialog: false,
      sendEmailTarget: null,
      sendEmailTargetClearTimer: null,
      sendEmailSuccessDialogTimer: null,
      sendEmailType: 'CREDENTIALS',
      processingSendEmail: false,
      showSendAccountEmailSuccessDialog: false,
      sendAccountEmailSuccessInfo: null,
      // True when the admin-only endpoints 403'd and we're falling back to the
      // legacy /tournaments/overview/{id} endpoint. In this mode fetchParticipants
      // re-calls overview instead of /admin/participants.
      useLegacyOverview: false,
      // SFB-1072 skin reveal: shown once per (tournament, participant) the first
      // time meAsParticipant is populated with a skin character.
      showSkinReveal: false,
      // Captured by beforeRouteEnter — drives the "go back to competitions"
      // CTA so the user lands on the same list they came from (club tab vs
      // global competitions list) instead of relying on global clubMember
      // state, which doesn't reset when the user leaves /club routes.
      referrerRouteName: null,
      /** Drives countdown reactivity (~1 Hz while REVIEWING banner visible). */
      reviewingCountdownTick: Date.now(),
      reviewingCountdownIntervalId: null,
      /** Drives the registration banner countdown (~1 Hz while visible). */
      registrationCountdownTick: Date.now(),
      registrationCountdownIntervalId: null,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.referrerRouteName = from?.name || null;
    });
  },
  computed: {
    parentBackgroundStyle() {
      return this.embedBackgroundColor
        ? { backgroundColor: this.embedBackgroundColor }
        : {};
    },
    // SFB-1205: removal is only offered (and only accepted by the backend)
    // before the competition starts.
    competitionNotStarted() {
      const startTime = this.$store.state.tournament?.startTime;
      return !!startTime && new Date(startTime) > new Date();
    },
    isRemoveSelected() {
      return this.disqualifyAction === 'DISQUALIFY'
        && this.competitionNotStarted
        && this.memberActionChoice === 'REMOVE';
    },
    // New computed property to check if we should show the minimal view
    showMinimalView() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return false;
      }

      const hasNameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_NAME');
      const hasUsernameFeature = selectedFeatures.includes('TOURNAMENT_RANKING_USERNAME');

      return !hasNameFeature && !hasUsernameFeature;
    },
    stoppedTournament() {
      if (this.participantsSearch) return 0; // search with no results ≠ stopped tournament
      const count = this.participantsTotalElements || this.$store.state.tournament?.participants?.length || 0;
      return (this.started || this.finished) && count < this.$store.state.tournament.minsize ? this.$store.state.tournament.minsize : 0;
    },
    inviteOnly() {
      return this.$store.state.tournament.inviteOnly && !this.started && !this.meAsParticipant;
    },
    tournamentStatus() {
      return this.getTournamentStatus();
    },
    /** Shown hours value in finalize banner description (interpolation). */
    reviewingPeriodHours() {
      return FINALIZE_REVIEW_WINDOW_HOURS;
    },
    /** Auto-close deadline: competition `endTime` + review window (see TD-012). */
    reviewingDeadlineMs() {
      const end = this.$store.state.tournament?.endTime;
      if (!end) return null;
      return new Date(end).getTime() + FINALIZE_REVIEW_WINDOW_MS;
    },
    showReviewingFinalizeBanner() {
      return (
        this.adminUser
        && this.adminMode
        && this.tournamentStatus === 'REVIEWING'
        && this.reviewingDeadlineMs != null
      );
    },
    reviewingCountdownParts() {
      const deadline = this.reviewingDeadlineMs;
      if (deadline == null) {
        return { days: '00', hours: '00', minutes: '00', seconds: '00' };
      }
      const diff = Math.max(0, deadline - this.reviewingCountdownTick);
      const totalSec = Math.floor(diff / 1000);
      return {
        days: padTwo(Math.floor(totalSec / 86400)),
        hours: padTwo(Math.floor((totalSec % 86400) / 3600)),
        minutes: padTwo(Math.floor((totalSec % 3600) / 60)),
        seconds: padTwo(totalSec % 60),
      };
    },
    /**
     * Finalize-banner colors: gradient from admin table accent (same source as leaderboard header).
     * @returns {Record<string,string>}
     */
    finalizeBannerStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      let clean = resolveAdminBannerHexClean(colors?.primary);
      if (!clean) {
        const clubColor = this.$store.state.clubMember?.club?.primaryColor;
        clean = resolveAdminBannerHexClean(clubColor);
      }
      if (!clean) clean = '009F63';

      const from = `#${clean}`;
      const r = parseInt(clean.substr(0, 2), 16);
      const g = parseInt(clean.substr(2, 2), 16);
      const b = parseInt(clean.substr(4, 2), 16);
      const lighten = (ch) => Math.round(ch + (255 - ch) * 0.45);
      const to = `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
      return {
        background: `linear-gradient(90deg, ${from} 0%, ${to} 100%)`,
        color: '#fff',
        '--finalize-banner-accent': from,
      };
    },
    /**
     * CSS vars for manual-closure modal actions — same accent chain as leaderboard / finalize banner.
     * @returns {Record<string,string>}
     */
    manualClosureDialogThemeStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      let clean = resolveAdminBannerHexClean(colors?.primary);
      if (!clean) {
        clean = resolveAdminBannerHexClean(this.$store.state.clubMember?.club?.primaryColor);
      }
      if (!clean) clean = '009F63';
      const accent = `#${clean}`;
      const label = this.getContrastYIQ(clean) === 'black' ? '#14161b' : '#ffffff';
      return {
        '--manual-closure-accent': accent,
        '--manual-closure-label': label,
      };
    },
    isPublicView() {
      return this.$route.name === 'tournament-embed-rank';
    },
    // Stricter than `adminUser`: the ended-tournament Overview tab exposes
    // aggregated participant stats that should only surface to the hosting
    // club's FOUNDER/ADMIN. Brokers who happen to be authenticated against
    // the broker app see the tournament page as admins (so they can manage
    // competitions) but aren't entitled to this club-scoped summary.
    isClubAdmin() {
      const tournament = this.$store.state.tournament;
      const member = this.$store.state.clubMember;
      if (!tournament || !member || !member.club) return false;
      if (tournament.tournamentOrganizerId !== member.club.id) return false;
      return member.rol === 'FOUNDER' || member.rol === 'ADMIN';
    },
    // Admin UI/tools only when the user is an admin AND has not toggled "View as User".
    effectiveAdminMode() {
      return this.adminUser && this.adminMode;
    },
    formattedRulesHtml() {
      if (!this.tournamentRules || !this.tournamentRules.length) {
        return null;
      }

      // Convert tournament rules array to HTML string for RulesDialog component
      let rulesHtml = `<p class="mb-5">${this.$t('rulesDescription')}</p><ul class="rules-list">`;

      this.tournamentRules.forEach(rule => {
        rulesHtml += `<li class="rule-item">
          <strong>${escapeHtml(rule.description)}</strong>: ${escapeHtml(rule.value)}
        </li>`;
      });

      rulesHtml += '</ul>';
      return rulesHtml;
    },
    // New computed property for organizer color styling
    endedTabsAccentColor() {
      // 1. Tournament branding colors
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      if (colors) {
        const isDark = this.$vuetify.theme.dark;
        const c = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (c) return `#${c.replace('#', '')}`;
      }
      // 2. Club color
      const clubColor = this.$store.state.clubMember?.club?.primaryColor;
      if (clubColor) return `#${clubColor.replace('#', '')}`;
      // 3. Fallback green
      return null;
    },
    endedTabsStyle() {
      const color = this.endedTabsAccentColor;
      if (!color) return {};
      const contrast = this.getContrastYIQ(color) === 'black' ? '#000' : '#fff';
      return {
        '--ended-tab-accent': color,
        '--ended-tab-contrast': contrast
      };
    },
    organizerColorStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      if (colors && (colors.primary || colors.secondary)) {
        const isDark = this.$vuetify.theme.dark;
        const color = isDark && colors.secondary ? colors.secondary : colors.primary;
        if (!color) return {};
        const cleanColor = color.replace('#', '');
        const fullColor = `#${cleanColor}`;
        return {
          backgroundColor: fullColor,
          borderColor: fullColor,
          color: this.getContrastYIQ(cleanColor) === 'black' ? '#000' : '#fff'
        };
      }
      return {};
    },
    shouldShowTimer() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_COUNTER');
    },
    showEndedTimerLabel() {
      const endTime = this.$store.state.tournament?.endTime;
      if (!endTime) return false;
      return new Date(endTime) < new Date();
    },
    shouldShowRules() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_RULES');
    },
    shouldShowFaqs() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_FAQS');
    },
    shouldShowAwards() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_AWARD');
    },
    shouldShowSponsor() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_SPONSORED');
    },
    hasAwardsContent() {
      const awards = this.$store.state.tournament?.awards || [];
      const partnerLogo = this.$store.state.tournament?.tournamentSettings?.imageUrl;
      return (this.shouldShowAwards && awards.length > 0)
        || (this.shouldShowSponsor && !!partnerLogo);
    },
    isSkinned() {
      return isSkinEnabled(this.$store.state.tournament);
    },
    meAsParticipantSkinned() {
      // meAsParticipant is a `data` field captured from a possibly pre-hydration
      // snapshot of the participants list, so its `.skin` can be empty even after
      // hydrateParticipants has run. Prefer the live store entry when the local
      // ref is missing the skin object — same pattern UserSummary uses.
      const me = this.meAsParticipant;
      if (!me) return null;
      if (me.skin && me.skin.name) return me;
      const live = this.$store.state.tournament?.participants?.find(
        (p) => p?.participantId === me.participantId,
      );
      return live || me;
    },
    // Resolves the current user's id/username across all three apps.
    // Trader populates $store.state.user; broker keeps its identity at
    // $store.state.broker.user; IB at $store.state.ib.user. The participant
    // lookup needs whichever one is non-null so the skin reveal (and other
    // "is this me?" checks) work in every host.
    currentUserId() {
      return this.$store.state.user?.id
        || this.$store.state.broker?.user?.id
        || this.$store.state.ib?.user?.id
        || null;
    },
    currentUsername() {
      return this.$store.state.user?.username
        || this.$store.state.broker?.user?.username
        || this.$store.state.ib?.user?.username
        || null;
    },
    // Whether the right column will render anything — used to collapse the
    // column so the leaderboard can span the full width. Rules/FAQs buttons
    // live in the user-facing footer, which admins see when previewing as user.
    hasRightColumnContent() {
      if (this.meAsParticipant) return true;
      if (this.rightColumnPrizePoolVisible) return true;
      if (this.hasAwardsContent) return true;
      if (this.shouldShowRules) return true;
      if (this.shouldShowFaqs && this.$store.state.tournament?.faqsUrl) return true;
      return false;
    },

    /**
     * Registration deadline: startTime while registration is open pre-start,
     * or startTime + daysPostRegister during the post-start window (TD source:
     * checkPostRegistrationWindow). Null when registration is not open.
     */
    registrationDeadlineMs() {
      const tournament = this.$store.state.tournament;
      if (!tournament || !tournament.startTime) return null;
      const start = new Date(tournament.startTime).getTime();
      const now = this.registrationCountdownTick;
      if (now < start) return start;
      if (this.inPostRegistrationWindow) {
        const days = Number(tournament.tournamentSettings?.daysPostRegister) || 0;
        if (days > 0) return start + days * 24 * 60 * 60 * 1000;
      }
      return null;
    },
    showRegistrationBanner() {
      if (!REGISTRATION_BANNER_ENABLED) return false;
      if (this.effectiveAdminMode || this.isEmbedView) return false;
      if (this.meAsParticipant) return false;
      if (this.$store.state.tournament?.inviteOnly) return false;
      if (this.stoppedTournament) return false;
      const deadline = this.registrationDeadlineMs;
      return deadline != null && deadline > this.registrationCountdownTick;
    },
    registrationDeadlineLabel() {
      const deadline = this.registrationDeadlineMs;
      if (deadline == null) return '';
      return new Date(deadline).toLocaleDateString(this.$i18n.locale, { day: '2-digit', month: 'short' });
    },
    registrationCountdownParts() {
      const deadline = this.registrationDeadlineMs;
      if (deadline == null) {
        return { days: '00', hours: '00', mins: '00', secs: '00' };
      }
      const diff = Math.max(0, deadline - this.registrationCountdownTick);
      const totalSec = Math.floor(diff / 1000);
      return {
        days: padTwo(Math.floor(totalSec / 86400)),
        hours: padTwo(Math.floor((totalSec % 86400) / 3600)),
        mins: padTwo(Math.floor((totalSec % 3600) / 60)),
        secs: padTwo(totalSec % 60),
      };
    },
    /** Banner background: partner primary → club color → swiset green (same chain as leaderboard header). */
    registrationBannerStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      let clean = resolveAdminBannerHexClean(colors?.primary);
      if (!clean) {
        clean = resolveAdminBannerHexClean(this.$store.state.clubMember?.club?.primaryColor);
      }
      if (!clean) clean = '009F63';
      return {
        backgroundColor: `#${clean}`,
        color: this.getContrastYIQ(clean) === 'black' ? '#14161b' : '#fff',
      };
    },
    registrationBannerCtaStyle() {
      const colors = this.$store.state.tournament?.tournamentSettings?.colors;
      let clean = resolveAdminBannerHexClean(colors?.primary);
      if (!clean) {
        clean = resolveAdminBannerHexClean(this.$store.state.clubMember?.club?.primaryColor);
      }
      if (!clean) clean = '009F63';
      return { color: `#${clean}` };
    },

    // Computed property for status chip styling with YIQ contrast
    statusChipStyle() {
      const status = this.tournamentStatus.toLowerCase().replace(' ', '-');
      let backgroundColor = '';

      switch (status) {
        case 'still-open':
          backgroundColor = 'FFF75F';
          break;
        case 'in-progress':
          // Use the green color value - you may need to adjust this to match your CSS variable
          backgroundColor = '00F784'; // This should match your --v-mainGreen-base value
          break;
        case 'reviewing':
          backgroundColor = 'FF9800'; // Orange color for pending/reviewing status
          break;
        case 'closed':
          backgroundColor = '111111';
          break;
        case 'archived':
          backgroundColor = '6c757d';
          break;
        default:
          backgroundColor = 'f8f8f8';
      }

      const textColor = this.getContrastYIQ(backgroundColor);

      return {
        color: textColor === 'black' ? '#000000' : '#ffffff'
      };
    },
  },
  async mounted() {
    // Listen for language change events
    i18nEventBus.$on('language-changed', () => {
      // Force re-render of the entire component tree by changing the key
      this.refreshKey += 1;
    });

    this.isEmbedView = this.$route.name === 'tournament-embed-rank';
    this.identifier = this.$route.query.identifier || null;
    await this.init();
    window.addEventListener("resize", () => {
      let mobile = window.innerWidth <= 1290 ? true : false;
      this.isMobile = mobile;
    });
    await this.getTournamentRules();
    if (!this.isEmbedView) {
      this.startTournamentScheduleWatcher();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'tournament-settings') {
      this.resetTournament();
    }
    next();
  },
  destroyed() {
    // Remove the event listener
    i18nEventBus.$off('language-changed');
    this.stopReviewingCountdownTicker();
    this.stopRegistrationCountdownTicker();
    this.stopTournamentScheduleWatcher();
    if (this.sendEmailTargetClearTimer != null) {
      clearTimeout(this.sendEmailTargetClearTimer);
      this.sendEmailTargetClearTimer = null;
    }
    if (this.sendEmailSuccessDialogTimer != null) {
      clearTimeout(this.sendEmailSuccessDialogTimer);
      this.sendEmailSuccessDialogTimer = null;
    }
    // Restore chat widget visibility if hidden
    if (this.overlayTournamentRules) {
      this.overlayTournamentRules = false;
    }
  },
  methods: {
    handleBackgroundColorChange(color) {
      this.embedBackgroundColor = color;
    },
    init() {
      this.overlayProcessing = true;

      this.isMobile = window.innerWidth <= 1290 ? true : false;

      // Reset any existing tournament state before loading new one
      // This prevents stale data from showing momentarily
      this.resetTournament();

      this.getTournamentOverview();
    },
    getTournamentOverview() {
      // Get tournament ID from query param, route param, or component prop
      // IMPORTANT: Do NOT fall back to store state - it may contain stale data
      let id = this.$route.query.tournamentId || this.$route.params.tournamentId;
      if (!id || id == null) {
        // Try to get ID from component prop (used by broker app wrapper)
        if (this.tournamentId !== undefined && this.tournamentId !== null) {
          id = this.tournamentId;
        }
      }

      // If we don't have an ID, go back instead of hard-redirecting
      if (!id) {
        console.error('No tournament ID available in query params, route params, or props');
        this.$router.back();
        return;
      }

      // Store the ID we're loading to detect race conditions (only for numeric IDs)
      const numericId = Number(id);
      const isNumericId = !isNaN(numericId) && isFinite(numericId);
      const loadingId = isNumericId ? numericId : id;
      
      // Only set currentLoadingTournamentId for numeric IDs to enable race condition guard
      if (isNumericId) {
        this.currentLoadingTournamentId = loadingId;
      } else {
        // For string IDs, reset to null to disable race condition guard
        this.currentLoadingTournamentId = null;
      }

      let isFromPublic = this.$route.name === 'tournament-embed-rank';
      this.isEmbedView = isFromPublic;

      // Reset participants pagination state
      this.participantsPage = 0;
      this.participantsTotalPages = 1;
      this.participantsTotalElements = 0;
      this.participantsSearch = '';
      this.participantsCurrentId = id;

      if (isFromPublic) {
        let identifier = this.$route.query.identifier;
        this.identifier = identifier;
        Repository.getTournamentPublicInfo(id).then((response) => {
          if (isNumericId && this.currentLoadingTournamentId !== loadingId) {
            return;
          }
          if (response.status < 400) {
            this.overlayProcessing = false;
            const normalizedData = this.normalizeInfoResponse(response.data);
            this.computeTournamentBasics(normalizedData);
            this.applyParticipantsCount(normalizedData.participantsCount);
            this.fetchParticipants(0, '');
            this.fetchCurrentUserParticipant();
          }
        });
        return;
      }

      // Private view: try the admin-only split info endpoint first. Non-admin participants
      // get 403 on that endpoint — fall back to the legacy full overview endpoint, which
      // still returns TournamentOverviewDTO (including skin fields on participants) for
      // any authenticated user.
      Repository.getTournamentAdminInfo(id).then((response) => {
        if (isNumericId && this.currentLoadingTournamentId !== loadingId) {
          return;
        }
        if (response.status < 400) {
          this.overlayProcessing = false;
          const normalizedData = this.normalizeInfoResponse(response.data);
          this.computeTournamentBasics(normalizedData);
          this.applyParticipantsCount(normalizedData.participantsCount);
          // Admin endpoints don't carry skin fields on participants (SFB-1073 gap).
          // When skin is enabled, source participants from the legacy overview endpoint
          // so admins and founders also see the skinned UI end-to-end.
          if (this.skinThemeEnabledOnTournament(normalizedData)) {
            this.useLegacyOverview = true;
          }
          this.fetchParticipants(0, '');
          this.fetchCurrentUserParticipant();
        }
      }).catch((err) => {
        if (isNumericId && this.currentLoadingTournamentId !== loadingId) return;
        if (err?.response?.status !== 403) {
          console.error('Failed to load tournament admin info:', err);
          return;
        }
        this.loadTournamentOverviewFallback(id, isNumericId, loadingId);
      });
    },
    skinThemeEnabledOnTournament(data) {
      if (!data) return false;
      const flag = data.tournamentSettings?.skinThemeEnabled
        ?? data.competitionSkinTheme?.enabled
        ?? data.skinThemeEnabled;
      return !!flag;
    },
    loadTournamentOverviewFallback(id, isNumericId, loadingId) {
      this.useLegacyOverview = true;
      Repository.getTournamentOverview(id).then((response) => {
        if (isNumericId && this.currentLoadingTournamentId !== loadingId) return;
        if (response.status >= 400) return;
        this.overlayProcessing = false;
        const data = response.data || {};
        const normalizedData = this.normalizeInfoResponse(data);
        this.computeTournamentBasics(normalizedData);
        // Overview response already carries participants; hydrate the store and
        // skip the separate paginated fetch (which would also 403 for non-admin).
        const participants = Array.isArray(data.participants) ? data.participants : [];
        this.$store.commit('setTournamentParticipants', participants);
        const count = data.participantsCount ?? participants.length;
        this.applyParticipantsCount(count);
        this.participantsTotalElements = count;
        this.participantsTotalPages = Math.max(1, Math.ceil(count / 10));
        this.participantsPage = 0;
        this.fetchCurrentUserParticipant();
      }).catch((err) => {
        console.error('Failed to load tournament overview fallback:', err);
      });
    },
    applyParticipantsCount(count) {
      if (!count) return;
      const PAGE_SIZE = 10;
      this.participantsTotalElements = count;
      this.participantsTotalPages = Math.ceil(count / PAGE_SIZE);
    },
    async fetchParticipants(page, search) {
      if (!this.participantsCurrentId) return;
      this.participantsLoading = true;
      try {
        const id = this.participantsCurrentId;
        const externalId = this.$store.state.tournament?.tournamentSettings?.externalId;
        let response;
        if (this.isEmbedView) {
          response = await Repository.getTournamentPublicParticipants(id, page, 10, search);
        } else if (this.useLegacyOverview) {
          // Non-admin fallback / skin-theme path: re-hit the legacy overview
          // endpoint; it returns all participants in one page, so paginate
          // client-side. Takes priority over the public-by-externalId branch
          // below because (a) the legacy response carries skin fields that the
          // public endpoint omits, and (b) when this flag is set due to a 403
          // from the admin info endpoint, the store was already hydrated from
          // the legacy call in init() — staying on the same source keeps
          // pagination consistent.
          response = await Repository.getTournamentOverview(id);
          if (response.status < 400) {
            const all = Array.isArray(response.data?.participants) ? response.data.participants : [];
            const filtered = search
              ? all.filter((p) => `${p.firstName || ''} ${p.lastName || ''} ${p.username || ''} ${p.email || ''} ${p.brokerAccountIdentifier || ''}`
                  .toLowerCase().includes(search.toLowerCase()))
              : all;
            const start = page * 10;
            const pageSlice = filtered.slice(start, start + 10);
            this.$store.commit('setTournamentParticipants', pageSlice);
            this.participantsTotalElements = filtered.length;
            this.participantsTotalPages = Math.max(1, Math.ceil(filtered.length / 10));
            this.participantsPage = page;
            return;
          }
        } else if (!this.effectiveAdminMode && externalId) {
          // User-mode auth view should mirror the public/embed leaderboard:
          // eligible-rank, ACTIVE-only, server-paginated. The admin endpoint
          // returns ALL statuses with GLOBAL rank, which is the wrong semantic
          // for user mode (and only authorized for ADMIN/FOUNDER anyway).
          // Admin tools view (effectiveAdminMode=true) keeps the admin endpoint below.
          response = await Repository.getTournamentPublicParticipants(externalId, page, 10, search);
        } else {
          response = await Repository.getTournamentAdminParticipants(id, page, 10, search);
        }
        if (response.status < 400) {
          const { participants, totalElements, totalPages, currentPage } = response.data;
          this.$store.commit('setTournamentParticipants', participants || []);
          this.participantsTotalElements = totalElements || 0;
          this.participantsTotalPages = totalPages || 1;
          this.participantsPage = currentPage || 0;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error fetching participants:', e);
      } finally {
        this.participantsLoading = false;
      }
    },
    onParticipantsPageChange(page) {
      this.fetchParticipants(page, this.participantsSearch);
    },
    // The participants overlay added/disqualified/activated someone (SFB-1217):
    // re-fetch the current page so the admin table and counters stay in sync.
    onOverlayParticipantsChanged() {
      this.fetchParticipants(this.participantsPage, this.participantsSearch);
    },
    onParticipantsSearchChange(query) {
      this.participantsSearch = query;
      this.fetchParticipants(0, query);
    },
    ...mapMutations(['setTournament', 'resetTournament', 'setShare', 'setTournamentParticipants']),
    normalizeInfoResponse(data) {
      // Handle the nested structure from the new info endpoints (no participants field)
      if (data.tournamentSettings && data.tournamentSettings.tournament) {
        const tournament = data.tournamentSettings.tournament;
        return {
          tournamentId: tournament.id,
          tournamentName: tournament.name,
          tournamentOrganizer: tournament.creator ? tournament.creator.name : '',
          tournamentOrganizerId: tournament.creator ? tournament.creator.id : null,
          startTime: tournament.startTime,
          endTime: tournament.endTime,
          imageUrl: tournament.imageUrl || data.tournamentSettings.imageUrl,
          faqsUrl: tournament.faqsUrl || data.tournamentSettings.faqsUrl || '',
          inviteOnly: tournament.inviteOnly,
          tournamentVisibility: tournament.tournamentVisibility,
          initialBalance: tournament.initialBalance,
          minsize: data.tournamentSettings.minsize,
          tournamentSettings: data.tournamentSettings,
          selectedFeatures: data.selectedFeatures || [],
          awards: data.awards || [],
          participants: [], // participants are fetched separately via fetchParticipants
          participantsCount: data.participantsCount || 0,
          secsAutoRefresh: tournament.assessment ? tournament.assessment.secsAutoRefresh : null
        };
      }
      // Fallback: data is already flat (old endpoint format)
      return {
        ...data,
        participants: data.participants || [],
      };
    },
    normalizeAdminResponse(data, isAdminCall) {
      // If this is from the admin endpoint and has the nested structure, normalize it
      if (isAdminCall && data.tournamentSettings && data.tournamentSettings.tournament) {
        const tournament = data.tournamentSettings.tournament;

        // Flatten the structure to match the regular endpoint format
        return {
          tournamentId: tournament.id,
          tournamentName: tournament.name,
          tournamentOrganizer: tournament.creator ? tournament.creator.name : '',
          tournamentOrganizerId: tournament.creator ? tournament.creator.id : null,
          startTime: tournament.startTime,
          endTime: tournament.endTime,
          imageUrl: tournament.imageUrl || data.tournamentSettings.imageUrl,
          faqsUrl: tournament.faqsUrl || data.tournamentSettings.faqsUrl || '',
          inviteOnly: tournament.inviteOnly,
          tournamentVisibility: tournament.tournamentVisibility,
          initialBalance: tournament.initialBalance,
          minsize: data.tournamentSettings.minsize,
          tournamentSettings: data.tournamentSettings,
          selectedFeatures: data.selectedFeatures || [],
          awards: data.awards || [],
          participants: data.participants || [],
          secsAutoRefresh: tournament.assessment ? tournament.assessment.secsAutoRefresh : null
        };
      }

      // Return data as-is if it's already in the correct format
      return data;
    },
    computeTournamentBasics(tournamentOverview) {
      this.setTournament(tournamentOverview);
      let now = new Date();

      // Set language based on tournament organizer ID
      this.setLanguageBasedOnTournament();

      if (new Date(this.$store.state.tournament.endTime) < now) { // Ended tournament
        this.finished = true;
      }
      else if (new Date(this.$store.state.tournament.startTime) < now) {// Running tournament
        this.started = true;

        // Check if we're in the post-registration window
        this.checkPostRegistrationWindow();

        if (tournamentOverview.secsAutoRefresh !== null && tournamentOverview.secsAutoRefresh > 60) {
          this.secsAutoRefresh = tournamentOverview.secsAutoRefresh;
          // eslint-disable-next-line no-console
          console.log(`Calling tournament overview at: ${(new Date())}`);
          this.setTimerRealTimeEmulation();
        }
      }

      this.verifyMeAsParticipant();
      this.verifyIfAdmin();
      this.handleActionParam();
    },
    checkPostRegistrationWindow() {
      const daysPostRegister = this.$store.state.tournament?.tournamentSettings?.daysPostRegister || 0;

      // Only check if daysPostRegister is set and greater than 0
      if (daysPostRegister > 0) {
        const now = new Date();
        const startTime = new Date(this.$store.state.tournament.startTime);
        const postRegistrationEndTime = new Date(startTime.getTime() + (daysPostRegister * 24 * 60 * 60 * 1000));

        // Only show post-registration window if:
        // 1. We're within the registration time window
        // 2. User is NOT yet a participant (if they are, show leaderboard instead)
        this.inPostRegistrationWindow = !this.effectiveAdminMode &&
          now >= startTime &&
          now <= postRegistrationEndTime &&
          !this.meAsParticipant;
      } else {
        this.inPostRegistrationWindow = false;
      }
    },
    maybeShowSkinReveal() {
      // SFB-1072: show the reveal dialog once when the current user's participant
      // record first arrives with an assigned skin character. Gated by localStorage
      // per (tournamentId, participantId) so the reveal doesn't replay on refresh,
      // closing/reopening the browser, or navigating away and back. Clearing
      // browser data or switching to a different device will re-show it — worth
      // knowing if we ever move this flag server-side.
      if (this.showSkinReveal) return;
      if (this.finished) return;
      if (!isSkinEnabled(this.$store.state.tournament)) return;
      // Use the skinned variant so we check the live store entry (meAsParticipant
      // itself can be a pre-hydration snapshot whose .skin is still empty).
      const me = this.meAsParticipantSkinned;
      if (!me || !me.skin || !me.skin.name) return;
      const tournamentId = this.$store.state.tournament?.tournamentId;
      const participantId = me.participantId;
      if (!tournamentId || !participantId) return;
      const key = `skinRevealed:${tournamentId}:${participantId}`;
      try {
        if (localStorage.getItem(key) === '1') return;
      } catch (_) { /* localStorage unavailable — show once, don't persist */ }
      this.showSkinReveal = true;
    },
    dismissSkinReveal() {
      this.showSkinReveal = false;
      const tournamentId = this.$store.state.tournament?.tournamentId;
      const participantId = this.meAsParticipant?.participantId;
      if (tournamentId && participantId) {
        try {
          localStorage.setItem(`skinRevealed:${tournamentId}:${participantId}`, '1');
        } catch (_) { /* ignore */ }
      }
    },
    verifyMeAsParticipant() {
      // Skip if we already resolved the current user's participation via dedicated fetch
      if (this.meAsParticipant) return;

      // Guard: tournament or participants not loaded yet
      if (!this.$store.state.tournament || !this.$store.state.tournament.participants) return;

      // Fallback: check within the currently loaded participants page
      let foundParticipant = null;

      if (this.identifier != null) {
        // Match by broker account identifier (public/embed view)
        foundParticipant = this.$store.state.tournament.participants.find(
          (participant) => participant.brokerAccountIdentifier === this.identifier);
      } else {
        // Try to match by userId first, then fallback to username. Resolve the
        // current user across trader/broker/IB so this works in every host.
        const userId = this.currentUserId;
        const username = this.currentUsername;
        if (userId) {
          foundParticipant = this.$store.state.tournament.participants.find(
            (participant) => participant.userId === userId);
        }
        if (!foundParticipant && username) {
          foundParticipant = this.$store.state.tournament.participants.find(
            (participant) => participant.username === username);
        }
      }

      if (foundParticipant) {
        this.meAsParticipant = foundParticipant;
      }

      // Re-check post-registration window when participant status changes
      if (this.started) {
        this.checkPostRegistrationWindow();
      }
    },
    async fetchCurrentUserParticipant() {
      // Dedicated lookup for the logged-in user's participation.
      // Uses the search endpoint with the user's username so we don't need
      // to iterate all pages — a single call returns their entry if it exists.
      if (!this.participantsCurrentId) return;
      const searchTerm = this.identifier || this.currentUsername;
      if (!searchTerm) return;

      try {
        let response;
        if (this.isEmbedView) {
          response = await Repository.getTournamentPublicParticipants(
            this.participantsCurrentId, 0, 10, searchTerm);
        } else if (this.useLegacyOverview) {
          // Legacy/skin mode: the overview endpoint returns the full roster
          // with skin fields. We must NOT reuse $store...participants here —
          // in legacy mode fetchParticipants stores only the current page
          // slice, so a participant ranked beyond page 1 would never be found
          // (their skin card would then only appear once the table is paged
          // to their rank). Always fetch the full list.
          const r = await Repository.getTournamentOverview(this.participantsCurrentId);
          response = { status: r.status, data: { participants: r.data?.participants || [] } };
        } else {
          response = await Repository.getTournamentAdminParticipants(
            this.participantsCurrentId, 0, 10, searchTerm);
        }
        if (response.status < 400 && response.data.participants) {
          const participants = response.data.participants;
          let found = null;

          if (this.identifier) {
            found = participants.find(p => p.brokerAccountIdentifier === this.identifier);
          } else {
            const userId = this.currentUserId;
            const username = this.currentUsername;
            if (userId) found = participants.find(p => p.userId === userId);
            if (!found && username) {
              found = participants.find(p => p.username === username);
            }
          }

          if (found) {
            // The legacy overview returns raw skin fields (skinCharacterName /
            // skinImageUrl) but not the built `.skin` object the UI reads —
            // hydrateParticipants only runs on participants committed to the
            // store. Build it here so meAsParticipant carries its skin
            // regardless of which leaderboard page is currently loaded.
            if (!found.skin && isSkinEnabled(this.$store.state.tournament)) {
              const builtSkin = buildSkinFromServer(found, getThemeCode(this.$store.state.tournament));
              if (builtSkin) found.skin = builtSkin;
            }
            // The legacy overview list has no server-computed `place`, but its
            // order IS the global ranking (and here it's the full roster, not
            // a page). Derive the user's place from it — the summary card must
            // stay correct when the user's row is outside the currently loaded
            // page or filtered out by search (the card's fallback is an index
            // lookup over the current page slice, which breaks under server
            // pagination). Skip non-legacy branches: their `participants` is a
            // search-result page whose index is not a rank, and their rows
            // carry a server-computed `place` already.
            if (this.useLegacyOverview
              && (found.place === undefined || found.place === null)) {
              const rankedIdx = participants.findIndex(
                (p) => p && p.participantId === found.participantId);
              if (rankedIdx !== -1) found.place = rankedIdx + 1;
            }
            this.meAsParticipant = found;
            if (this.started) {
              this.checkPostRegistrationWindow();
            }
          }
        }
      } catch (e) {
        // Silent fail — meAsParticipant stays null (user not enrolled)
      }
    },
    verifyIfAdmin() {
      this.adminUser = false;
      const tournament = this.$store.state.tournament;
      const member = this.$store.state.clubMember;
      if (tournament !== null && member !== null && member.club) {
        this.adminUser = tournament.tournamentOrganizerId === member.club.id &&
          (member.rol === 'FOUNDER' || member.rol === 'ADMIN');
      }
      if (this.$store.getters['broker/authToken']) {
        this.adminUser = true;
      }
      if (!this.adminUser) {
        this.adminMode = false;
      }
    },
    handleActionParam() {
      const action = this.$route.query.action;
      if (!action || !this.adminUser) return;

      this.adminMode = true;

      if (action === 'share') {
        // Clean the action param, then open share overlay
        const query = { ...this.$route.query };
        delete query.action;
        this.$router.replace({ query });
        this.$nextTick(() => {
          this.overlayTournamentShare = true;
        });
      } else if (action === 'settings') {
        // Replace the current history entry so back navigation skips the overview with ?action=settings.
        // tournamentId must be in query (not only params) so the URL survives refresh — the route
        // path has no :tournamentId segment, and params are in-memory only.
        const tournament = this.$store.state.tournament;
        this.$router.replace({
          name: 'tournament-settings',
          params: {
            tournamentId: tournament.tournamentId,
            tournamentSummary: tournament
          },
          query: {
            tournamentId: tournament.tournamentId,
          }
        });
      }
    },
    setTimerRealTimeEmulation() {
      setTimeout(this.getTournamentOverview, (this.secsAutoRefresh * 1000))
    },
    startTournamentScheduleWatcher() {
      this.stopTournamentScheduleWatcher();

      let watchedBeforeStart = false;
      let watchedBeforeEnd = false;

      const tick = () => {
        const startTime = this.$store.state.tournament?.startTime;
        const endTime = this.$store.state.tournament?.endTime;
        if (!startTime || !endTime) return;

        const now = Date.now();
        const startMs = new Date(startTime).getTime();
        const endMs = new Date(endTime).getTime();
        const toStart = startMs - now;
        const toEnd = endMs - now;

        if (toStart > 0) watchedBeforeStart = true;
        if (now >= startMs && toEnd > 0) watchedBeforeEnd = true;

        if (watchedBeforeStart && toStart <= 0 && now < endMs) {
          this.stopTournamentScheduleWatcher();
          window.location.reload();
          return;
        }

        if (watchedBeforeEnd && toEnd <= 0) {
          this.stopTournamentScheduleWatcher();
          window.location.reload();
        }
      };

      tick();
      this.tournamentScheduleInterval = setInterval(tick, 1000);
    },
    stopTournamentScheduleWatcher() {
      if (this.tournamentScheduleInterval) {
        clearInterval(this.tournamentScheduleInterval);
        this.tournamentScheduleInterval = null;
      }
    },
    seeDistribution() {
      this.overlayTradesDistribution = true;
    },
    tournamentSettings() {
      this.overlayTournamentSettings = true;
    },
    tournamentParticipants() {
      this.overlayTournamentParticipants = true;
    },
    showCopiedSnackbar() {
      this.snackbarText = this.$t('copiedToClipboard');
      this.snackbarColor = 'darkGreen';
      this.snackbar = true;
    },
    handleEmailSent(email) {
      this.snackbarText = this.$t('emailSentSuccess', { email });
      this.snackbarColor = 'darkGreen';
      this.snackbar = true;
      this.overlayTournamentShare = false;
    },
    handleEmailError(errorMsg) {
      this.snackbarText = errorMsg;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
    getTournamentStatus() {
      // Check if tournament has a specific state (like REVIEWING)
      const tournamentState = this.$store.state.tournament?.tournamentSettings?.tournamentState;
      if (tournamentState === 'REVIEWING') {
        return 'REVIEWING';
      }

      const now = new Date();
      const startTime = new Date(this.$store.state.tournament.startTime);
      const endTime = new Date(this.$store.state.tournament.endTime);

      if (now < startTime) {
        return 'STILL OPEN';
      } else if (now >= startTime && now <= endTime) {
        return 'IN PROGRESS';
      } else {
        return 'CLOSED';
      }
    },
    getRuleNamenValue(rule) {
      switch (rule.ruleType) {
        case 'MAX_DRAWDOWN_BL_PT':
        case 'MAX_DRAWDOWN_RT':
        case 'MAX_DRAWDOWN':
          // Convert decimal to percentage (e.g., 0.3 -> 30%)
          return {
            name: this.$t('maxDrawdown'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'CONSISTENCY':
          // Convert decimal to percentage (e.g., 0.4 -> 40%)
          return {
            name: this.$t('consistency'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'MIN_TRADES':
          return {
            name: this.$t('minTrades'),
            value: rule.value
          };
        case 'OVERNIGHT':
          // Check if overnight trading is allowed
          return {
            name: this.$t('overnight'),
            value: rule.value > 0 ? this.$t('yes') : this.$t('no')
          };
        case 'POSITIVE_BALANCE':
          // Format as currency
          return {
            name: this.$t('positiveBalance'),
            value: `USD $${rule.value.toFixed(2)}`
          };
        case 'MAX_TRADE_SIZE':
          // Format large numbers with commas for readability
          return {
            name: this.$t('maxTradeSize'),
            value: `${rule.value.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${this.$t('lots')}`
          };
        case 'MAX_DRAWDOWN_EQ_PT':
          // Max. Equity Drawdown - percentage (e.g., 0.3 -> 30%)
          return {
            name: this.$t('maxEquityDrawdown'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'POSITIVE_EQUITY':
          // Boolean: positive equity required
          return {
            name: this.$t('positiveEquity'),
            value: rule.value >=0 ? this.$t('yes') : this.$t('no')
          };
        case 'MIN_TRADABLE_DAYS':
          return {
            name: this.$t('minTradableDays'),
            value: rule.value
          };
        case 'HIGH_IMPACT_NEWS_TRADING_FXSTREET':
          return {
            name: this.$t('rules_TournamentRules.dynamic.highImpactNewsTradingRestrictionFxstreet'),
            value: rule.value > 0 ? this.$t('yes') : this.$t('no')
          };
        case 'HIGH_IMPACT_NEWS_TRADING_FOREX_FACTORY':
          return {
            name: this.$t('rules_TournamentRules.dynamic.highImpactNewsTradingRestrictionForexFactory'),
            value: rule.value > 0 ? this.$t('yes') : this.$t('no')
          };
        case 'MAX_DAILY_LOSS_EOD':
          return {
            name: this.$t('maxDailyLossEodRuleLabel'),
            value: `${(rule.value * 100).toFixed(0)}%`
          };
        case 'MIN_AGGREGATED_TRADE_SIZE':
          return {
            name: this.$t('minAggregatedTradeSizeRuleLabel'),
            value: `${rule.value.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${this.$t('lots')}`
          };
        case 'MIN_TRADE_SIZE':
          return {
            name: this.$t('minTradeSizeRuleLabel'),
            value: `${rule.value.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${this.$t('lots')}`
          };
        case 'NO_MID_COMPETITION_DEPOSITS':
          return {
            name: this.$t('noMidCompetitionDepositsRuleLabel'),
            value: rule.value > 0 ? this.$t('yes') : this.$t('no')
          };
        default:
          return {
            name: rule.ruleType,
            value: rule.value
          };
      }
    },
    async getTournamentRules() {
      // Guard: Don't fetch rules if tournament is not loaded yet
      if (!this.$store.state.tournament || !this.$store.state.tournament.tournamentId) {
        return;
      }

      let rules;
      await Repository.getTournamentRules(this.$store.state.tournament.tournamentId).then((res) => {
        rules = res.data.map(rule => {
          let ruleFormatted = this.getRuleNamenValue(rule)
          return {
            ruleId: rule.ruleType,
            description: ruleFormatted.name,
            value: ruleFormatted.value,
          }
        });
      });
      this.tournamentRules = rules;
    },
    goToFaqs() {
      window.open(this.$store.state.tournament.faqsUrl, '_blank');
    },
    showNoFaqsMessage() {
      this.snackbarText = this.$t('noFaqsConfigured') || 'No FAQs URL configured for this tournament';
      this.snackbarColor = 'warning';
      this.snackbar = true;
    },
    async handleBracketChange(bracket) {
      // When bracket changes, fetch the tournament info for the selected bracket's tournament
      this.overlayProcessing = true;
      try {
        const response = await Repository.getTournamentAdminInfo(bracket.tournamentId);

        if (response.status === 200) {
          const tournamentData = response.data;
          const normalizedData = this.normalizeInfoResponse(tournamentData);

          // For brackets, use master tournament info from the bracket data
          const masterName = bracket.masterName;
          const masterStartTime = bracket.tournamentStartTime;
          const masterEndTime = bracket.tournamentEndTime;
          const originalStatus = this.$store.state.tournament.tournamentStatus;
          const originalOrganizer = this.$store.state.tournament.tournamentOrganizer;
          const originalImageUrl = this.$store.state.tournament.imageUrl;

          // Update tournament data with bracket-specific info
          this.setTournament({
            ...normalizedData,
            tournamentId: bracket.tournamentId,
            tournamentName: masterName,
            startTime: masterStartTime,
            endTime: masterEndTime,
            tournamentStatus: originalStatus,
            tournamentOrganizer: originalOrganizer,
            imageUrl: originalImageUrl,
            participants: [],
          });

          // Update pagination state and fetch participants for this bracket
          this.participantsCurrentId = bracket.tournamentId;
          this.applyParticipantsCount(normalizedData.participantsCount);
          this.fetchParticipants(0, '');

          this.$forceUpdate();
        }
      } catch (error) {
        console.error('Error fetching bracket tournament data:', error);
      } finally {
        this.overlayProcessing = false;
      }
    },
    updateTournamentData(tournamentData) {
      // Update tournament data from TournamentEmbedView when bracket changes
      this.setTournament(tournamentData);
      this.$forceUpdate();
    },
    // Helper method to determine if text should be black or white based on background color
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';

      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');

      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);

      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      return (yiq >= 128) ? 'black' : 'white';
    },
    setLanguageBasedOnTournament() {
      const tournamentId = this.$store.state.tournament?.tournamentId;
      const targetLocale = this.$store.state.tournament?.tournamentSettings?.mainLanguage;
      if (!tournamentId || !targetLocale) {
        return;
      }

      const languageSetKey = `language_set_for_tournament_${tournamentId}`;

      if (localStorage.getItem(languageSetKey) === 'true') {
        return;
      }

      if (localStorage.getItem(`user_changed_locale_tournament_${tournamentId}`)) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      if (this.$i18n.locale === targetLocale) {
        localStorage.setItem(languageSetKey, 'true');
        return;
      }

      localStorage.setItem(languageSetKey, 'true');
      setLocale(targetLocale);
      this.$store.commit('setPreferredLanguage', targetLocale);

      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    confirmManualClosure() {
      this.showClosureConfirmation = true;
    },
    startReviewingCountdownTicker() {
      this.stopReviewingCountdownTicker();
      this.reviewingCountdownTick = Date.now();
      this.reviewingCountdownIntervalId = setInterval(() => {
        this.reviewingCountdownTick = Date.now();
      }, 1000);
    },
    stopReviewingCountdownTicker() {
      if (this.reviewingCountdownIntervalId != null) {
        clearInterval(this.reviewingCountdownIntervalId);
        this.reviewingCountdownIntervalId = null;
      }
    },
    startRegistrationCountdownTicker() {
      this.stopRegistrationCountdownTicker();
      this.registrationCountdownTick = Date.now();
      this.registrationCountdownIntervalId = setInterval(() => {
        this.registrationCountdownTick = Date.now();
      }, 1000);
    },
    stopRegistrationCountdownTicker() {
      if (this.registrationCountdownIntervalId != null) {
        clearInterval(this.registrationCountdownIntervalId);
        this.registrationCountdownIntervalId = null;
      }
    },
    /** Registration banner CTA: bring the registration module (not-started / post-start card) into view. */
    scrollToRegistration() {
      const col = this.$refs.mainContentCol;
      const el = col && (col.$el || col);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    goToTournamentSettings() {
      const tournament = this.$store.state.tournament;
      // Pass both the tournament summary and the tournament ID for reliability
      this.$router.push({
          name: 'tournament-settings',
          params: {
            tournamentId: tournament.tournamentId,
            tournamentSummary: tournament
          }
      });
    },
    goBackToCompetitions() {
      // Entry-point detection priority:
      //   1. ?fromClub / ?fromClubId stamped by ClubTournaments — survives refresh.
      //   2. navigationReferrer from route wrappers, else referrerRouteName from
      //      beforeRouteEnter when this component is the routed view (trader app).
      // Avoid \$store.state.clubMember as a signal; it persists globally and
      // misroutes anyone who opened a competition from the global list while
      // a stale club context was loaded.
      const fromClubId = this.$route.query?.fromClubId;
      const fromClubQuery = this.$route.query?.fromClub === '1';
      const referrer = this.navigationReferrer || this.referrerRouteName || '';
      const fromClub = fromClubQuery || !!fromClubId || referrer.includes('club');
      if (this.$isBrokerApp) {
        this.$router.push({
          name: fromClub ? 'broker-club-tournaments' : 'broker-competitions',
        });
        return;
      }
      if (this.$isIBApp) {
        this.$router.push({ name: 'ib-dashboard' });
        return;
      }
      this.$router.push({
        name: fromClub ? 'club-tournaments' : 'tournament-home',
      });
    },
    async manuallyCloseTournament() {
      this.closingTournament = true;
      const tournamentId = this.$store.state.tournament.tournamentId;

      try {
        // Call API to update tournament state to FINISHED
        const response = await Repository.updateTournamentState(tournamentId, 'FINISHED');

        if (response.status < 400) {
          this.snackbarText = this.$t('tournamentClosed');
          this.snackbarColor = 'success';
          this.snackbar = true;
          this.showClosureConfirmation = false;

          // Refresh tournament data
          setTimeout(() => {
            this.getTournamentOverview();
          }, 1000);
        } else {
          throw new Error('Failed to close tournament');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error closing tournament:', error);
        this.snackbarText = this.$t('errorClosingTournament');
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.closingTournament = false;
      }
    },
    // SFB-1199: closed competitions no longer allow manual activation or
    // disqualification. The action buttons are hidden (Table.vue), so these
    // handler guards are defense in depth against stale UI. Same check the
    // admin Leaderboard's tournamentEnded binding uses.
    competitionHasEnded() {
      return new Date(this.$store.state.tournament.endTime) <= new Date();
    },
    onDisqualifyParticipant(participant) {
      if (this.competitionHasEnded()) return;
      this.disqualifyTarget = participant;
      this.disqualifyAction = 'DISQUALIFY';
      this.disqualificationReasonInput = '';
      this.memberActionChoice = 'DISQUALIFY';
      this.showDisqualifyDialog = true;
    },
    onActivateParticipant(participant) {
      if (this.competitionHasEnded()) return;
      this.disqualifyTarget = participant;
      this.disqualifyAction = 'ACTIVATE';
      this.disqualificationReasonInput = '';
      this.showDisqualifyDialog = true;
    },
    async executeStatusChange() {
      if (!this.disqualifyTarget) return;
      if (this.competitionHasEnded()) return;
      if (this.isRemoveSelected) {
        return this.executeRemoveParticipant();
      }
      this.processingAction = true;
      const newStatus = this.disqualifyAction === 'DISQUALIFY' ? 'DISQUALIFIED' : 'ACTIVE';
      try {
        const disqualificationReason =
          this.disqualifyAction === 'DISQUALIFY' ? this.disqualificationReasonInput : undefined;
        const response = await Repository.changeParticipantStatus(
          this.disqualifyTarget.participantId,
          newStatus,
          disqualificationReason,
        );
        if (response.status < 400) {
          this.snackbarText = this.disqualifyAction === 'DISQUALIFY'
            ? (this.$t('participantDisqualified') || 'Participant disqualified')
            : (this.$t('participantActivated') || 'Participant activated');
          this.snackbarColor = 'mainGreen';
          this.snackbar = true;
          this.showDisqualifyDialog = false;
          // Refresh tournament data
          this.getTournamentOverview();
        }
      } catch (error) {
        console.error('Error changing participant status:', error);
        this.snackbarText = this.$t('errorChangingStatus') || 'Error changing participant status';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.processingAction = false;
      }
    },
    // SFB-1205: permanent removal — same popup, different backend path. The
    // backend re-validates the pre-start window and answers 409 otherwise.
    async executeRemoveParticipant() {
      this.processingAction = true;
      try {
        const response = await Repository.removeTournamentParticipant(
          this.$store.state.tournament.tournamentId,
          this.disqualifyTarget.participantId,
          this.disqualificationReasonInput,
        );
        if (response.status < 400) {
          this.snackbarText = this.$t('participantRemoved');
          this.snackbarColor = 'mainGreen';
          this.snackbar = true;
          this.showDisqualifyDialog = false;
          this.getTournamentOverview();
        }
      } catch (error) {
        this.snackbarText = error?.response?.status === 409
          ? this.$t('removeOnlyBeforeStart')
          : (this.$t('errorChangingStatus') || 'Error changing participant status');
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.processingAction = false;
      }
    },
    onSeeJournal(participant) {
      let sharingDetails = {
        id: participant.userId,
        username: participant.username,
        imageUrl: participant.imageUrl,
        firstName: participant.firstName,
        lastName: participant.lastName,
        country: participant.country,
        allowComments: false,
      };
      this.setShare(sharingDetails);
      this.$router.push('/book/shared');
    },
    sendAccountEmail(participant) {
      if (!participant) return;
      if (this.sendEmailSuccessDialogTimer != null) {
        clearTimeout(this.sendEmailSuccessDialogTimer);
        this.sendEmailSuccessDialogTimer = null;
      }
      this.sendEmailTarget = participant;
      this.sendEmailType = 'CREDENTIALS';
      this.showSendAccountEmailDialog = true;
    },
    onSendAccountEmailDialogToggle(isOpen) {
      if (this.sendEmailTargetClearTimer != null) {
        clearTimeout(this.sendEmailTargetClearTimer);
        this.sendEmailTargetClearTimer = null;
      }
      if (!isOpen) {
        this.sendEmailTargetClearTimer = setTimeout(() => {
          this.sendEmailTarget = null;
          this.sendEmailTargetClearTimer = null;
        }, 320);
      }
    },
    closeSendAccountEmailSuccessDialog() {
      this.showSendAccountEmailSuccessDialog = false;
      this.sendAccountEmailSuccessInfo = null;
    },
    parseSendAccountEmailResponse(responseData, isErrorResponse = false) {
      const data = responseData || {};
      const details = data.details || {};
      const results = Array.isArray(details.results) ? details.results : [];
      const first = results[0] || {};
      const cred = first.credentials || {};
      const welcome = first.welcome || {};
      const hasBatchStats =
        details &&
        (details.success !== undefined ||
          details.failed !== undefined);
      const batchOk =
        !hasBatchStats ||
        (Number(details.failed) === 0 && Number(details.success) > 0);
      const credentialStepOk = !cred.status || cred.status === 'SUCCESS';
      const welcomeStepOk = !welcome.status || welcome.status === 'SUCCESS';
      const credOk = credentialStepOk && welcomeStepOk;
      const successFallback =
        this.$t('sendAccountEmailSuccess') || 'Email sent successfully';
      const credentialsMessage =
        cred.message != null && String(cred.message).trim()
          ? String(cred.message).trim()
          : null;
      const welcomeMessage =
        welcome.message != null && String(welcome.message).trim()
          ? String(welcome.message).trim()
          : null;
      return {
        batchOk,
        credOk,
        message:
          data.message ||
          (isErrorResponse ? null : successFallback),
        detail: credentialsMessage || welcomeMessage || null,
        credentialsMessage,
        welcomeMessage,
        account:
          first.account !== undefined && first.account !== null
            ? String(first.account)
            : null,
      };
    },
    sendAccountEmailErrorSnackbarText(payload) {
      const parsed =
        payload != null && typeof payload === 'object'
          ? this.parseSendAccountEmailResponse(payload, true)
          : null;
      return (
        (parsed &&
          (parsed.credentialsMessage ||
            parsed.welcomeMessage ||
            parsed.detail ||
            parsed.message)) ||
        (typeof payload === 'string' ? payload : null) ||
        (payload &&
          typeof payload === 'object' &&
          typeof payload.message === 'string' &&
          payload.message) ||
        this.$t('sendAccountEmailError') ||
        'Could not send the email. Please try again.'
      );
    },
    async executeSendAccountEmail() {
      if (!this.sendEmailTarget) return;
      const tournamentId = this.$store.state.tournament?.tournamentId;
      const participantId = this.sendEmailTarget.participantId;
      if (!tournamentId || participantId == null) {
        this.snackbarText = this.$t('sendAccountEmailError') || 'Could not send the email. Please try again.';
        this.snackbarColor = 'error';
        this.snackbar = true;
        return;
      }
      this.processingSendEmail = true;
      const participantName =
        this.sendEmailTarget.firstName ||
        this.sendEmailTarget.username ||
        '';
      const participantMailRaw = this.sendEmailTarget.email;
      const participantMail =
        participantMailRaw != null && String(participantMailRaw).trim()
          ? String(participantMailRaw).trim()
          : null;
      try {
        const response = await Repository.sendAccountEmail(tournamentId, {
          participantId,
          type: this.sendEmailType,
        });
        const parsed = this.parseSendAccountEmailResponse(response.data);
        const httpOk = response.status < 400;
        if (httpOk && parsed.batchOk && parsed.credOk) {
          if (this.sendEmailSuccessDialogTimer != null) {
            clearTimeout(this.sendEmailSuccessDialogTimer);
            this.sendEmailSuccessDialogTimer = null;
          }
          this.showSendAccountEmailDialog = false;
          let credentialsMessage = parsed.credentialsMessage;
          let welcomeMessage = parsed.welcomeMessage;
          if (
            credentialsMessage &&
            welcomeMessage &&
            credentialsMessage === welcomeMessage
          ) {
            welcomeMessage = null;
          }
          this.sendAccountEmailSuccessInfo = {
            message: parsed.message,
            credentialsMessage,
            welcomeMessage,
            account: parsed.account,
            participantName,
            mail: participantMail,
          };
          this.sendEmailSuccessDialogTimer = setTimeout(() => {
            this.showSendAccountEmailSuccessDialog = true;
            this.sendEmailSuccessDialogTimer = null;
          }, 120);
        } else if (httpOk) {
          this.showSendAccountEmailDialog = false;
          this.snackbarText =
            parsed.credentialsMessage ||
            parsed.welcomeMessage ||
            parsed.detail ||
            parsed.message ||
            this.$t('sendAccountEmailError') ||
            'Could not send the email. Please try again.';
          this.snackbarColor = 'error';
          this.snackbar = true;
        } else {
          this.showSendAccountEmailDialog = false;
          this.snackbarText = this.sendAccountEmailErrorSnackbarText(response.data);
          this.snackbarColor = 'error';
          this.snackbar = true;
        }
      } catch (error) {
        console.error('Error sending account email:', error);
        this.showSendAccountEmailDialog = false;
        const errData = error.response && error.response.data;
        this.snackbarText = this.sendAccountEmailErrorSnackbarText(errData);
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.processingSendEmail = false;
      }
    },
    async sendParticipantsEmail() {
      const id = this.$store.state.tournament?.tournamentId;
      if (!id) return;
      this.exportLoading = true;
      try {
        const response = await Repository.sendTournamentParticipantsEmail(id);

        if (response.status < 400) {
          this.snackbarText =
            this.$t('participantsEmailSent') ||
            'The participants information will be sent to your email.';
          this.snackbarColor = 'mainGreen';
          this.snackbar = true;
        }
      } catch (e) {
        console.error('Error sending participants email:', e);
        this.snackbarText =
          this.$t('participantsEmailError') ||
          'We could not send the participants information by email.';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.exportLoading = false;
      }
    }
  },
  render(h) {
    return h('div', { key: this.refreshKey }, [
      h('keep-alive', this.$slots.default)
    ]);
  }
}
</script>

<style lang="scss" scoped>
.tournament-rank-view {
  font-family: "Avenir", sans-serif;

  .tournament-timer {
    @media screen and (max-width: 600px) {
      justify-content: center;
    }

    justify-content: flex-end;
  }

  // Keep existing styles for tournament-rank-embed
  &.tournament-rank-embed {
    padding: 0;
    margin: 0;

    // Hide unnecessary elements in embed view
    .admin-controls-bar,
    .admin-table-toolbar,
    .additional-desktop {
      display: none !important;
    }
  }

  /* New improved styles similar to tournament-embed-view */
  .tournament-embed-header {
    position: relative;
    padding-bottom: 30px !important;

    .tournament-title {
      font-family: 'Aeonik', 'Avenir', sans-serif;
      font-size: 30px;
      font-weight: 700;
      line-height: 40px;
      color: var(--v-mainBlack-base);
      margin: 0;
    }

    .tournament-organizer {
      font-size: 16px;
      font-weight: 400;
      margin-top: 5px;
      color: var(--v-mainBlack-base);

      .tournament-organizer__name {
        font-weight: 800;
      }
    }

    /* Figma info bar: title block left, countdown timer right */
    .header-info-bar {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 40px;
      margin-top: 16px;

      &__left {
        flex: 1 1 auto;
        min-width: 0;
      }

      &__timer {
        flex: 0 0 auto;
        width: 308px;
        max-width: 40%;
      }
    }

    /* Figma registration banner: partner-colored strip + white CTA */
    .registration-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      flex-wrap: wrap;
      border-radius: 8px;
      padding: 10px 16px;
      margin-top: 24px;

      &__texts {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }

      &__title {
        font-family: 'Aeonik', 'Avenir', sans-serif;
        font-size: 18px;
        font-weight: 500;
        line-height: 1.5;
        margin: 0;
      }

      &__countdown {
        font-size: 14px;
        font-weight: 300;
        line-height: 1.5;
        margin: 0;
        opacity: 0.95;
      }

      &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 36px;
        padding: 10px 16px;
        background-color: #fff;
        border: 1px solid #fff;
        border-radius: 8px;
        font-family: 'Aeonik', 'Avenir', sans-serif;
        font-size: 18px;
        font-weight: 500;
        cursor: pointer;
        white-space: nowrap;
        transition: transform 0.15s ease;

        &:hover {
          transform: translateY(-1px);
        }
      }
    }

    .tournament-ended-label-overview {
      margin-top: 12px;

      ::v-deep .tournament-timer {
        text-align: left;
        justify-content: flex-start;
      }

      ::v-deep .tournament-timer__ended-text {
        text-align: left;
      }
    }

    /* Enhanced Tournament Timer Container */
    .tournament-timer-container {
      margin-top: -96px;
      margin-bottom: 100px;

      // Small tablet breakpoint (sm: 600px - 768px)
      @media (min-width: 600px) and (max-width: 768px) {
        margin-top: 10px;
        margin-bottom: 40px;
      }

      // Large tablet breakpoint (md: 768px - 960px)
      @media (min-width: 768px) and (max-width: 960px) {
        margin-top: -20px;
        margin-bottom: 60px;
      }

      // Mobile breakpoint
      @media (max-width: 600px) {
        margin-top: 20px;
        margin-bottom: 20px;
      }
    }

    /* Ended tournaments: neutralize timer offsets so tabs/summary stay below header */
    &.is-finished .tournament-timer-container {
      margin-top: 20px;
      margin-bottom: 24px;

      @media (min-width: 600px) and (max-width: 768px) {
        margin-top: 20px;
        margin-bottom: 24px;
      }

      @media (min-width: 768px) and (max-width: 960px) {
        margin-top: 20px;
        margin-bottom: 24px;
      }

      @media (max-width: 600px) {
        margin-top: 20px;
        margin-bottom: 24px;
      }
    }

    /* Admin controls - redesigned bar matching Figma */
    .admin-controls-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      margin-bottom: 8px;

      .admin-controls-left {
        display: flex;
        align-items: center;
      }

      .admin-controls-right {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .admin-breadcrumb {
        background: none;
        border: none;
        cursor: pointer;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        color: #888;
        display: flex;
        align-items: center;
        padding: 4px 0;
        transition: color 0.2s ease;

        &:hover {
          color: #333;
        }

        i {
          font-size: 13px;
          color: #888;
        }
      }

      .admin-icon-btn {
        background: none;
        border: none;
        width: 36px;
        height: 36px;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #ababab;
        transition: color 0.2s ease;

        &:hover:not(:disabled) {
          color: #333;
        }

        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        i {
          font-size: 16px;
        }
      }

      .admin-divider {
        display: inline-block;
        width: 1px;
        height: 20px;
        background-color: #ddd;
        margin: 0 4px;
      }

      /* "View as User" / "View as Admin" — light pill button (Figma) */
      .admin-view-toggle {
        background-color: #ebebeb;
        color: #14161B;
        border: none;
        border-radius: 8px;
        height: 36px;
        padding: 10px 16px;
        font-family: 'Aeonik', sans-serif;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: all 0.2s ease;
        white-space: nowrap;

        &:hover {
          background-color: #ddd;
        }

        i {
          font-size: 14px;
        }
      }
    }

    /* REVIEWING: gradient + accent from finalizeBannerStyle (:style), matches leaderboard admin header tint. */
    .finalize-competition-banner {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      padding: 20px;
      border-radius: 12px;
      font-family: 'Avenir', 'Aeonik', sans-serif;
      box-sizing: border-box;
      margin-top: 8px;
      color: #fff !important;

      &__main {
        flex: 1 1 260px;
        min-width: 0;
      }

      &__title {
        font-size: 22px;
        font-weight: 700;
        margin: 0 0 8px;
        line-height: 1.2;
        color: #fff !important;
      }

      &__desc {
        font-size: 14px;
        line-height: 1.45;
        margin: 0 0 16px;
        opacity: 0.98;
        color: #fff !important;
      }

      &__cta {
        background: #fff;
        color: var(--finalize-banner-accent, #009f63);
        border: none;
        border-radius: 8px;
        padding: 10px 18px;
        font-size: 14px;
        font-weight: 600;
        font-family: inherit;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        transition: background 0.2s ease, transform 0.15s ease;

        &:hover {
          background: #fafafa;
        }

        &:active {
          transform: scale(0.98);
        }
      }

      &__countdown {
        display: flex;
        flex-shrink: 0;
        gap: 10px;
      }

      &__unit {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-width: 56px;
        padding: 10px 8px;
        border-radius: 8px;
        border: 1px solid rgba(255, 255, 255, 0.55);
        background: rgba(255, 255, 255, 0.12);
      }

      &__num {
        font-size: 26px;
        font-weight: 700;
        line-height: 1;
        color: #fff !important;
      }

      &__label {
        font-size: 11px;
        text-transform: capitalize;
        margin-top: 6px;
        opacity: 0.95;
        letter-spacing: 0.02em;
        color: #fff !important;
      }

      @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;

        &__countdown {
          justify-content: center;
          flex-wrap: wrap;
        }
      }
    }
  }

  /* Ended admin: tabs fixed above summary regardless of timer/async load */
  .ended-admin-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    clear: both;
    position: relative;
    z-index: 2;
    gap: 24px;
    margin-top: 8px;
  }

  .ended-admin-section__summary {
    width: 100%;
    order: 2;
  }

  /* Ended tournament view toggle — glider pattern */
  .ended-view-toggle {
    order: 1;
    flex-shrink: 0;
    margin-bottom: 0;
  }

  .ended-view-tabs.tabs {
    display: inline-flex;
    position: relative;
    background-color: var(--v-lightGray-base, #ebebeb);
    border-radius: 10px;

    * {
      z-index: 2;
    }

    input[type="radio"] {
      display: none;
    }

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 140px;
      height: 38px;
      font-size: 14px;
      font-family: 'Avenir', sans-serif;
      font-weight: 500;
      color: var(--v-mainBlack-base, #3b3b3b);
      border-radius: 10px;
      cursor: pointer;
      transition: color 0.15s ease-in;

      i {
        font-size: 14px;
      }
    }

    input[type="radio"]:checked + label {
      color: var(--ended-tab-contrast, #000) !important;
    }

    .glider {
      position: absolute;
      display: flex;
      height: 38px;
      width: 140px;
      background-color: var(--ended-tab-accent, var(--v-mainGreen-base, #00F784));
      border-radius: 10px;
      z-index: 1;
      transition: 0.25s ease-out;
    }

    input#ended-overview-tab:checked ~ .glider {
      transform: translateX(0);
    }

    input#ended-members-tab:checked ~ .glider {
      transform: translateX(100%);
    }
  }

  /* Disqualify confirm dialog */
  .disqualify-confirm-dialog {
    font-family: 'Avenir', sans-serif;

    .member-action-hint {
      font-size: 12px;
      opacity: 0.7;
      line-height: 1.3;
    }

    .remove-caution {
      font-size: 14px;
    }
  }

  /* Send-email modal radios: no ripple delay / icon tween */
  .send-email-type-radios {
    ::v-deep(.v-input--selection-controls__ripple) {
      transition: none !important;
      animation: none !important;
      opacity: 0 !important;
    }

    ::v-deep(.v-radio .v-icon) {
      transition: none !important;
      animation: none !important;
    }

    ::v-deep(.v-input--selection-controls__input) {
      transition: none !important;
    }
  }

  /* Status Chip Styling */
  .status-chip {
    padding: 3px 12px;
    border-radius: 18px;
    font-size: 14px;
    text-transform: capitalize;
    white-space: nowrap;

    &.still-open {
      background-color: #FFF75F;
    }

    &.in-progress {
      background-color: #D7FFED;
      color: #005931 !important;
    }

    &.reviewing {
      background-color: #FF9800;
    }

    &.closed {
      background-color: #111111;
    }

    &.archived {
      background-color: #6c757d;
    }
  }

  /* Organizer Logo Styling */
  .organizer-logo-container {
    margin-bottom: 20px;

    .organizer-logo {
      max-height: 80px;
      max-width: 250px;
      object-fit: contain;
    }
  }

  /* Mobile Tournament Header Styling */
  .mobile-tournament-header {
    padding: 0 15px;
    position: relative;
    margin-bottom: 20px;

    .mobile-tournament-title-container {
      margin: 20px 0 15px;
      position: relative;
    }

    .mobile-tournament-title {
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      margin: 0;
      padding: 0 10px 10px;
      color: var(--v-mainBlack-base);
      line-height: 1.3;
      word-wrap: break-word;
    }

    .mobile-title-underline {
      height: 3px;
      width: 60px;
      background: var(--v-mainGreen-base);
      margin: 5px auto 0;
      border-radius: 3px;
    }

    .mobile-tournament-organizer {
      font-size: 15px;
      text-align: center;
      color: #666;
      margin-bottom: 5px;

      .organizer-name {
        color: var(--v-mainBlack-base);
        font-weight: 500;
      }
    }

    .mobile-admin-controls {
      display: flex;
      justify-content: center;
      margin: 12px 0 8px;
    }

    .organizer-logo-container {
      margin-top: 10px;

      .organizer-logo {
        max-height: 60px;
        max-width: 200px;
        object-fit: contain;
      }
    }
  }

  /* Mobile Leaderboard Header */
  .mobile-leaderboard-header {
    width: 100%;
    text-align: center;

    .leaderboard-title-container {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 5px;

      i {
        font-size: 18px;
        color: var(--v-mainBlack-base);
        margin-right: 8px;
      }

      h2 {
        font-size: 20px !important;
        font-weight: bold;
        margin: 0;
      }
    }
  }

  /* Mobile Footer */
  .mobile-embed-footer {
    margin-top: 20px;
    width: 100%;
    background-color: transparent;

    .footer-actions {
      display: flex;
      justify-content: space-around;
      margin-bottom: 50px;

      .footer-action-btn {
        background-color: transparent !important;
        border: 1px solid #e0e0e0;
        padding: 12px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
        min-width: 120px;

        &:active {
          transform: translateY(1px);
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
        }

        i {
          font-size: 16px;
        }
      }
    }
  }

  /* Desktop Footer - new styles */
  .desktop-embed-footer {
    margin-top: 20px;

    .desktop-action-btn {
      background-color: var(--v-fullWhite-base);
      border: 1px solid #e0e0e0;
      padding: 10px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      text-align: center;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        opacity: 0.9;
      }

      i {
        font-size: 16px;
      }
    }
  }

  /* No participant message */
  .no-participant-message {
    text-align: center;
    background-color: var(--v-lightGray-base);
    border-radius: 8px;
    padding: 16px;
    margin: 0 0 16px;

    p {
      margin: 0;
      font-size: 16px;
    }
  }

  /* Responsive utilities */
  .hide-on-mobile {
    @media (max-width: 600px) {
      display: none !important;
    }
  }

  .show-on-mobile {
    @media (min-width: 601px) {
      display: none !important;
    }
  }

  /* Rules drawer styles */
  .rules-drawer {
    z-index: 99999 !important;

    @media (max-width: 600px) {
      width: 100% !important;
    }
  }

  /* Share drawer styles */
  .share-drawer {
    z-index: 99999 !important;

    @media (max-width: 600px) {
      width: 100% !important;
    }
  }
}

</style>

<style lang="scss">
/* Tournament manual-close modal — MUST be unscoped: Vuetify v-dialog overlays render outside scoped [data-v] subtree */
@mixin tournamentManualClosureRules {
  .manual-closure-dialog {
    font-family: "Avenir", "Aeonik", sans-serif;
    overflow: hidden;
    border-radius: 12px !important;

    .manual-closure-dialog__inner {
      padding: 24px 24px 8px;
    }

    .manual-closure-dialog__header {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: flex-start !important;
      justify-content: space-between !important;
      gap: 12px;
    }

    .manual-closure-dialog__title {
      flex: 1 1 auto !important;
      min-width: 0 !important;
      padding-right: 8px !important;
      padding-top: 2px !important;
      margin: 0 !important;
      font-family: "Aeonik", "Avenir", sans-serif !important;
      font-weight: 700 !important;
      font-size: 18px !important;
      line-height: 1.35 !important;
      color: var(--v-mainBlack-base) !important;
    }

    .manual-closure-dialog__close {
      flex: 0 0 auto !important;
      width: 36px !important;
      height: 36px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 0 !important;
      margin: -2px -2px 0 0 !important;
      border: none !important;
      border-radius: 8px !important;
      background: transparent !important;
      box-shadow: none !important;
      color: var(--v-mainBlack-base) !important;
      cursor: pointer !important;

      &:hover {
        background-color: rgba(0, 0, 0, 0.06) !important;
      }

      i {
        font-size: 18px !important;
        line-height: 1 !important;
      }
    }

    .manual-closure-dialog__divider {
      margin-top: 12px !important;
      margin-bottom: 18px !important;
      border-color: rgba(0, 0, 0, 0.08) !important;

      &::before {
        border-color: inherit !important;
      }
    }

    .manual-closure-dialog__body .manual-closure-dialog__message {
      margin: 0 !important;
      padding: 0 !important;
      font-family: inherit !important;
      font-size: 15px !important;
      font-weight: 400 !important;
      line-height: 1.5 !important;
      color: var(--v-mainBlack-base, #212121) !important;
    }

    .manual-closure-dialog__emphasis {
      display: inline !important;
      font-weight: 700 !important;
    }

    .manual-closure-dialog__actions.v-card__actions {
      border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
      justify-content: center !important;
      align-items: center !important;
      flex-wrap: wrap !important;
      gap: 12px !important;
    }

    /* Shared sizing / typography — variant colors below */
    .manual-closure-dialog__action-btn.manual-closure-dialog__action-btn.v-btn {
      box-shadow: none !important;
      text-transform: none !important;
      letter-spacing: 0 !important;
      font-weight: 600 !important;
      min-height: 40px !important;
      padding: 0 20px !important;

      &::before {
        background-color: currentColor !important;
      }

      .v-btn__content {
        color: inherit !important;
      }

      &:hover:not(.v-btn--disabled) {
        filter: brightness(0.93);
      }
    }

    .manual-closure-dialog__action-btn--confirm.manual-closure-dialog__action-btn--confirm.v-btn {
      background-color: var(--manual-closure-accent, var(--v-mainGreen-base, #009f63)) !important;
      border-width: thin !important;
      border-style: solid !important;
      border-color: transparent !important;
      color: var(--manual-closure-label, #14161b) !important;

      &:hover:not(.v-btn--disabled) {
        filter: brightness(0.93);
      }
    }

    .manual-closure-dialog__action-btn--cancel.manual-closure-dialog__action-btn--cancel.v-btn {
      background-color: #fff !important;
      border-width: thin !important;
      border-style: solid !important;
      border-color: rgba(0, 0, 0, 0.18) !important;
      color: var(--v-mainBlack-base, #212121) !important;

      &:hover:not(.v-btn--disabled) {
        filter: none !important;
        background-color: rgba(0, 0, 0, 0.04) !important;
      }
    }
  }
}

.trader-app .v-application,
.trader-app,
body.broker-app .v-application,
body.broker-app,
body.ib-app .v-application,
body.ib-app {
  @include tournamentManualClosureRules;
}

/* Dialog may mount under body without .v-application in the ancestry — duplicate selectors cover both. */

/* Dark mode overrides */
.theme--dark {
  .manual-closure-dialog .manual-closure-dialog__title {
    color: rgba(255, 255, 255, 0.95) !important;
  }

  .manual-closure-dialog .manual-closure-dialog__close {
    color: rgba(255, 255, 255, 0.9) !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.08) !important;
      color: #fff !important;
    }
  }

  .manual-closure-dialog .manual-closure-dialog__divider {
    border-color: rgba(255, 255, 255, 0.12) !important;
  }

  .manual-closure-dialog .manual-closure-dialog__body .manual-closure-dialog__message {
    color: rgba(255, 255, 255, 0.9) !important;
  }

  .manual-closure-dialog .manual-closure-dialog__actions.v-card__actions {
    border-top-color: rgba(255, 255, 255, 0.08) !important;
  }

  .manual-closure-dialog .manual-closure-dialog__action-btn--cancel.manual-closure-dialog__action-btn--cancel.v-btn {
    background-color: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.25) !important;
    color: rgba(255, 255, 255, 0.95) !important;

    &:hover:not(.v-btn--disabled) {
      background-color: rgba(255, 255, 255, 0.12) !important;
    }
  }

  .admin-view-toggle {
    background-color: #303740 !important;
    color: #fff !important;

    &:hover {
      background-color: #3a424d !important;
    }
  }
}

/* Neutral dialog cancel — match postcss-scoped globals (.trader-app/.broker-app .v-application .v-btn …) */
.trader-app .v-application .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn,
body.broker-app .v-application .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn,
body.ib-app .v-application .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn {
  background-color: transparent !important;
  background: transparent !important;
  color: var(--v-mediumGray-base) !important;
  box-shadow: none !important;

  .v-btn__content {
    color: inherit !important;
  }

  &:hover:not(.v-btn--disabled) {
    background-color: rgba(0, 0, 0, 0.06) !important;
    color: var(--v-mediumGray-base) !important;
  }
}

.trader-app .v-application.theme--dark .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn:hover:not(.v-btn--disabled),
body.broker-app .v-application.theme--dark .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn:hover:not(.v-btn--disabled),
body.ib-app .v-application.theme--dark .tournament-dialog-cancel-btn.v-btn.v-btn.v-btn:hover:not(.v-btn--disabled) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

/* Send-account-email dialog cancel — same treatment as broker MemberDrawer neutral cancel */
.trader-app .v-application .tournament-send-email-cancel-btn.v-btn.v-btn.v-btn,
body.broker-app .v-application .tournament-send-email-cancel-btn.v-btn.v-btn.v-btn,
body.ib-app .v-application .tournament-send-email-cancel-btn.v-btn.v-btn.v-btn {
  background-color: #ebebeb !important;
  color: #14161b !important;
  box-shadow: none !important;

  .v-btn__content {
    color: inherit !important;
  }

  &:hover:not(.v-btn--disabled) {
    background-color: #dfdfdf !important;
    color: #14161b !important;
  }
}

</style>